import { supabase } from '../lib/supabase';

// Customer Operations
export const customerService = {
  // Get all customers for the organization
  async getCustomers(organizationId, filters = {}) {
    try {
      let query = supabase?.from('customers')?.select(`
          *,
          invoices:invoices(count),
          quotes:quotes(count),
          creator:user_profiles!customers_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.search) {
        query = query?.or(`customer_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%,company_name.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching customers:', error);
      return { data: [], error };
    }
  },

  // Create new customer with enhanced error handling
  async createCustomer(customerData, organizationId, userId) {
    try {
      // Clean customerData to remove relation fields and non-column fields
      const cleanedData = { ...customerData };

      // Remove relation fields that come from SELECT queries
      delete cleanedData?.invoices;
      delete cleanedData?.quotes;
      delete cleanedData?.creator;

      // Remove duplicate/computed fields
      delete cleanedData?.name; // 'name' is duplicate of 'customer_name'

      // Remove fields that should be auto-generated or set by the service
      delete cleanedData?.id;
      delete cleanedData?.customer_number; // Will be generated
      delete cleanedData?.created_at; // Will be set by service
      delete cleanedData?.updated_at; // Will be set by service
      delete cleanedData?.created_by; // Will be set by service
      delete cleanedData?.organization_id; // Will be set by service

      // Fix invalid status values - customer_status enum only allows: 'active', 'inactive', 'suspended', 'archived'
      // 'draft' is for quotes/invoices, not customers
      if (cleanedData?.status === 'draft' || !cleanedData?.status) {
        cleanedData.status = 'active'; // Default to 'active' for new customers
      }

      // Validate status is one of the valid customer_status enum values
      const validCustomerStatuses = ['active', 'inactive', 'suspended', 'archived'];
      if (cleanedData?.status && !validCustomerStatuses?.includes(cleanedData?.status)) {
        console.warn(`Invalid customer status "${cleanedData?.status}", defaulting to "active"`);
        cleanedData.status = 'active';
      }

      // First, generate the customer number using the database function
      const { data: customerNumberData, error: numberError } = await supabase
        ?.rpc('generate_customer_number', { org_id: organizationId });

      if (numberError) {
        console.error('Error generating customer number:', numberError);
        // If function fails, generate a fallback customer number
        const fallbackNumber = `CUST-${Date.now()}`;
        console.warn('Using fallback customer number:', fallbackNumber);

        const { data, error } = await supabase?.from('customers')?.insert({
          ...cleanedData,
          customer_number: fallbackNumber,
          organization_id: organizationId,
          created_by: userId,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.select()?.single();

        if (error) throw error;
        return { data, error: null };
      }

      const customerNumber = customerNumberData;

      // Create the customer with the generated customer number
      const { data, error } = await supabase?.from('customers')?.insert({
        ...cleanedData,
        customer_number: customerNumber, // Add the required customer_number field
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.select()?.single();

      if (error) {
        // If primary insert fails due to customer_number constraint, try with fallback
        if (error?.code === '23502' && error?.message?.includes('customer_number')) {
          console.warn('Customer number constraint error, retrying with fallback');
          const fallbackNumber = `CUST-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 5)}`;

          const { data: retryData, error: retryError } = await supabase?.from('customers')?.insert({
            ...cleanedData,
            customer_number: fallbackNumber,
            organization_id: organizationId,
            created_by: userId,
            created_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString()
          })?.select()?.single();

          if (retryError) throw retryError;
          return { data: retryData, error: null };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating customer:', error);
      return { data: null, error };
    }
  },

  // Update customer
  async updateCustomer(customerId, updates, organizationId) {
    try {
      // CRITICAL FIX: Add UUID validation to prevent undefined errors
      if (!customerId || customerId === 'undefined' || customerId === null || customerId === '') {
        console.error('Invalid customer ID provided:', customerId);
        return {
          data: null,
          error: {
            message: 'Invalid customer ID provided. Cannot update customer without a valid ID.',
            code: 'INVALID_CUSTOMER_ID',
            details: 'Customer ID must be a valid UUID, but received: ' + customerId
          }
        };
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(customerId)) {
        console.error('Invalid UUID format for customer ID:', customerId);
        return {
          data: null,
          error: {
            message: 'Invalid customer ID format. Must be a valid UUID.',
            code: 'INVALID_UUID_FORMAT',
            details: 'Customer ID must be a valid UUID format, but received: ' + customerId
          }
        };
      }

      // CRITICAL FIX: Remove any fields that might cause schema cache errors
      const cleanedUpdates = { ...updates };

      // Remove profile data that causes PGRST204 error - these don't exist as columns
      delete cleanedUpdates?.creator; // FIXED: Remove creator alias (not a column)
      delete cleanedUpdates?.invoices; // Remove invoice count (not a column)
      delete cleanedUpdates?.quotes; // Remove quotes count (not a column)

      // CRITICAL FIX: Remove generated column that can only be updated to DEFAULT
      delete cleanedUpdates?.name; // FIXED: Remove auto-generated name column (not updatable)

      // Remove system fields that shouldn't be updated
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      delete cleanedUpdates?.customer_number; // Don't update customer number (immutable)

      // Add proper timestamp for update
      cleanedUpdates.updated_at = new Date()?.toISOString();

      console.log('CUSTOMER UPDATE - Customer ID:', customerId);
      console.log('CUSTOMER UPDATE - Organization ID:', organizationId);
      console.log('CUSTOMER UPDATE - Original data:', updates);
      console.log('CUSTOMER UPDATE - Cleaned data:', cleanedUpdates);

      const { data, error } = await supabase
        ?.from('customers')
        ?.update(cleanedUpdates)
        ?.eq('id', customerId)
        ?.eq('organization_id', organizationId)
        ?.select(`
          *,
          invoices:invoices(count),
          quotes:quotes(count),
          creator:user_profiles!customers_created_by_fkey(full_name)
        `)
        ?.single();

      if (error) {
        // ENHANCED: Handle 22P02 UUID errors specifically
        if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
          console.error('UUID validation error - invalid customer ID format:', error);
          return {
            data: null,
            error: {
              message: 'Invalid UUID format provided for customer ID.',
              code: 'UUID_VALIDATION_ERROR',
              details: 'The customer ID must be a valid UUID format. Please ensure the ID is properly formatted.',
              originalError: error
            }
          };
        }

        // ENHANCED: Handle 428C9 generated column errors specifically
        if (error?.code === '428C9') {
          console.error('Generated column error - column cannot be updated:', error);
          return {
            data: null,
            error: {
              message: 'Generated column error: The "name" column is auto-generated and cannot be updated directly.',
              code: 'GENERATED_COLUMN_ERROR',
              details: 'Generated columns can only be updated to DEFAULT. This column is automatically maintained by the database.',
              originalError: error
            }
          };
        }

        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error - column not found in customers table:', error);
          return {
            data: null,
            error: {
              message: 'Schema error: Referenced column does not exist in the customers table. This is a database configuration issue.',
              code: 'SCHEMA_ERROR',
              details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.',
              originalError: error
            }
          };
        }

        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating customer:', error);

      // CRITICAL: Handle 22P02 UUID errors with specific messaging
      if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
        return {
          data: null,
          error: {
            message: 'Invalid UUID format provided for customer ID.',
            code: 'UUID_VALIDATION_ERROR',
            details: 'The customer ID must be a valid UUID format. Please ensure the ID is properly formatted and not "undefined".'
          }
        };
      }

      // CRITICAL: Handle 428C9 generated column errors with specific messaging
      if (error?.code === '428C9') {
        return {
          data: null,
          error: {
            message: 'Generated column error: The "name" column is auto-generated and cannot be updated directly.',
            code: 'GENERATED_COLUMN_ERROR',
            details: 'Generated columns can only be updated to DEFAULT. This column is automatically maintained by the database.'
          }
        };
      }

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Schema error: Referenced column does not exist in the customers table. This is a database configuration issue.',
            code: 'SCHEMA_ERROR',
            details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // Delete customer with constraint checking
  async deleteCustomer(customerId, organizationId) {
    try {
      // Check for related records that would prevent deletion
      const constraintChecks = await Promise.all([
        // Check invoices
        supabase?.from('invoices')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check quotes
        supabase?.from('quotes')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check payments
        supabase?.from('payments_received')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check payment links
        supabase?.from('payment_links')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check delivery challans
        supabase?.from('delivery_challans')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check retainer invoices
        supabase?.from('retainer_invoices')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check recurring invoices
        supabase?.from('recurring_invoices')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check credit notes
        supabase?.from('credit_notes')?.select('id')?.eq('customer_id', customerId)?.limit(1),
        // Check bills
        supabase?.from('bills')?.select('id')?.eq('customer_id', customerId)?.limit(1)
      ]);

      const constraints = [];
      const [invoices, quotes, payments, paymentLinks, deliveryChallans, retainerInvoices, recurringInvoices, creditNotes, bills] = constraintChecks;

      if (invoices?.data?.length > 0) constraints?.push('invoices');
      if (quotes?.data?.length > 0) constraints?.push('quotes');
      if (payments?.data?.length > 0) constraints?.push('payments received');
      if (paymentLinks?.data?.length > 0) constraints?.push('payment links');
      if (deliveryChallans?.data?.length > 0) constraints?.push('delivery challans');
      if (retainerInvoices?.data?.length > 0) constraints?.push('retainer invoices');
      if (recurringInvoices?.data?.length > 0) constraints?.push('recurring invoices');
      if (creditNotes?.data?.length > 0) constraints?.push('credit notes');
      if (bills?.data?.length > 0) constraints?.push('bills');

      if (constraints?.length > 0) {
        return {
          data: null,
          error: {
            type: 'constraint_violation',
            message: `Cannot delete customer. Has related ${constraints?.join(', ')}.`,
            constraints
          }
        };
      }

      // Safe to delete - no foreign key constraints
      const { error } = await supabase?.from('customers')?.delete()?.eq('id', customerId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting customer:', error);
      return { data: null, error };
    }
  },

  // Force delete customer (cascades to all related records)
  async forceDeleteCustomer(customerId, organizationId) {
    try {
      // This will cascade delete all related records due to foreign key constraints
      const { error } = await supabase?.from('customers')?.delete()?.eq('id', customerId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true, cascaded: true }, error: null };
    } catch (error) {
      console.error('Error force deleting customer:', error);
      return { data: null, error };
    }
  },

  // Get customer details for viewing
  async getCustomerDetails(customerId, organizationId) {
    try {
      const { data, error } = await supabase?.from('customers')?.select(`
          *,
          customer_addresses(*),
          customer_contacts(*),
          invoices(count),
          quotes(count),
          payments_received(count),
          creator:user_profiles!customers_created_by_fkey(full_name, email)
        `)?.eq('id', customerId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching customer details:', error);
      return { data: null, error };
    }
  },

  // Get customers for dropdown/selector (simplified query for better performance)
  async getCustomersForDropdown(organizationId) {
    try {
      const { data, error } = await supabase?.from('customers')?.select(`
          id,
          customer_name,
          customer_number,
          email,
          status
        `)?.eq('organization_id', organizationId)?.eq('status', 'active')?.order('customer_name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching customers for dropdown:', error);
      return { data: [], error };
    }
  }
};

// Vendor Operations
export const vendorService = {
  // Get all vendors for the organization
  async getVendors(organizationId, filters = {}) {
    try {
      let query = supabase?.from('vendors')?.select(`
          *,
          bills:bills(count),
          creator:user_profiles!vendors_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.search) {
        query = query?.or(`vendor_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%,company_name.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching vendors:', error);
      return { data: [], error };
    }
  },

  // Get vendors for dropdown/selector (simplified query)
  async getVendorsForDropdown(organizationId) {
    try {
      const { data, error } = await supabase?.from('vendors')?.select(`
          id,
          vendor_name,
          vendor_number,
          email,
          status
        `)?.eq('organization_id', organizationId)?.eq('status', 'active')?.order('vendor_name', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching vendors for dropdown:', error);
      return { data: [], error };
    }
  },

  // Create new vendor
  async createVendor(vendorData, organizationId, userId) {
    try {
      // First, generate the vendor number using the database function
      const { data: vendorNumberData, error: numberError } = await supabase
        ?.rpc('generate_vendor_number', { org_id: organizationId });

      if (numberError) {
        console.error('Error generating vendor number:', numberError);
        // If function fails, generate a fallback vendor number
        const fallbackNumber = `VEND-${Date.now()}`;
        console.warn('Using fallback vendor number:', fallbackNumber);

        const { data, error } = await supabase?.from('vendors')?.insert({
          ...vendorData,
          vendor_number: fallbackNumber,
          organization_id: organizationId,
          created_by: userId,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.select()?.single();

        if (error) throw error;
        return { data, error: null };
      }

      const vendorNumber = vendorNumberData;

      // Create the vendor with the generated vendor number
      const { data, error } = await supabase?.from('vendors')?.insert({
        ...vendorData,
        vendor_number: vendorNumber,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.select()?.single();

      if (error) {
        // If primary insert fails due to vendor_number constraint, try with fallback
        if (error?.code === '23502' && error?.message?.includes('vendor_number')) {
          console.warn('Vendor number constraint error, retrying with fallback');
          const fallbackNumber = `VEND-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 5)}`;

          const { data: retryData, error: retryError } = await supabase?.from('vendors')?.insert({
            ...vendorData,
            vendor_number: fallbackNumber,
            organization_id: organizationId,
            created_by: userId,
            created_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString()
          })?.select()?.single();

          if (retryError) throw retryError;
          return { data: retryData, error: null };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating vendor:', error);
      return { data: null, error };
    }
  },

  // Update vendor
  async updateVendor(vendorId, updates, organizationId) {
    try {
      const { data, error } = await supabase?.from('vendors')?.update({
        ...updates,
        updated_at: new Date()?.toISOString()
      })?.eq('id', vendorId)?.eq('organization_id', organizationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating vendor:', error);
      return { data: null, error };
    }
  },

  // Delete vendor
  async deleteVendor(vendorId, organizationId) {
    try {
      // Check for related records that would prevent deletion
      const { data: relatedBills } = await supabase?.from('bills')?.select('id')?.eq('vendor_id', vendorId)?.limit(1);

      if (relatedBills?.length > 0) {
        return {
          data: null,
          error: {
            type: 'constraint_violation',
            message: 'Cannot delete vendor. Has related bills.',
            constraints: ['bills']
          }
        };
      }

      const { error } = await supabase?.from('vendors')?.delete()?.eq('id', vendorId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting vendor:', error);
      return { data: null, error };
    }
  },

  // Get vendor details for viewing
  async getVendorDetails(vendorId, organizationId) {
    try {
      const { data, error } = await supabase?.from('vendors')?.select(`
          *,
          bills(count),
          creator:user_profiles!vendors_created_by_fkey(full_name, email)
        `)?.eq('id', vendorId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      return { data: null, error };
    }
  }
};

// Enhanced Invoice Service with Line Items Support
export const invoiceService = {
  async getInvoices(organizationId, filters = {}) {
    try {
      // CRITICAL FIX: Use customer_id for joins, not customer column (which doesn't exist)
      let query = supabase?.from('invoices')?.select(`*,customer:customers!invoices_customer_id_fkey(*),invoice_line_items(*,item:items(*),account:chart_of_accounts(*))`)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters using correct column names
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.customer_id) {
        // CRITICAL FIX: Use customer_id column name consistently
        query = query?.eq('customer_id', filters?.customer_id);
      }
      if (filters?.dateFrom) {
        query = query?.gte('issue_date', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('issue_date', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) {
        // CRITICAL FIX: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error in getInvoices - column not found:', error);

          // Retry with simplified query to avoid column reference issues
          const { data: fallbackData, error: fallbackError } = await supabase
            ?.from('invoices')?.select('*')?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

          if (fallbackError) throw fallbackError;

          return {
            data: fallbackData || [],
            error: {
              type: 'schema_cache_warning', message: 'Retrieved invoices with simplified query due to schema cache error',
              originalError: error
            }
          };
        }
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return { data: [], error };
    }
  },

  async getInvoiceDetails(invoiceId, organizationId) {
    try {
      // CRITICAL FIX: Use explicit foreign key reference to avoid schema cache issues
      const { data, error } = await supabase?.from('invoices')?.select(`
          *,
          customer:customers!invoices_customer_id_fkey(*),
          invoice_line_items(
            *,
            item:items(*),
            account:chart_of_accounts(*)
          )
        `)?.eq('id', invoiceId)?.eq('organization_id', organizationId)?.single();

      if (error) {
        // CRITICAL FIX: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error in getInvoiceDetails - column not found:', error);

          // Retry with manual join to avoid schema cache issues
          const { data: invoiceData, error: invoiceError } = await supabase
            ?.from('invoices')
            ?.select('*')
            ?.eq('id', invoiceId)
            ?.eq('organization_id', organizationId)
            ?.single();

          if (invoiceError) throw invoiceError;

          // Manually fetch customer data using customer_id
          if (invoiceData?.customer_id) {
            const { data: customerData } = await supabase
              ?.from('customers')
              ?.select('*')
              ?.eq('id', invoiceData?.customer_id)
              ?.single();

            invoiceData.customer = customerData;
          }

          return {
            data: invoiceData,
            error: {
              type: 'schema_cache_warning',
              message: 'Retrieved invoice with manual join due to schema cache error',
              originalError: error
            }
          };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      return { data: null, error };
    }
  },

  // CRITICAL FIX: Enhanced helper function to get default revenue account for invoice line items
  async getDefaultRevenueAccount(organizationId) {
    try {
      console.log('INVOICE LINE ITEM FIX - Looking for revenue account for org:', organizationId);

      // STEP 1: Look for existing revenue accounts (preferably "Sales Revenue" or similar)
      const { data: revenueAccounts, error } = await supabase?.from('chart_of_accounts')
        ?.select('id, account_name, account_code')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'revenue')
        ?.eq('status', 'active')
        ?.order('account_code', { ascending: true })
        ?.limit(10); // Increased limit for better account selection

      if (error) {
        console.error('INVOICE LINE ITEM FIX - Error fetching revenue accounts:', error);
        return null;
      }

      console.log('INVOICE LINE ITEM FIX - Found revenue accounts:', revenueAccounts?.length || 0);

      if (revenueAccounts && revenueAccounts?.length > 0) {
        // Prefer accounts with "Sales", "Revenue", or "Income" in the name
        const preferredAccount = revenueAccounts?.find(acc =>
          acc?.account_name?.toLowerCase()?.includes('sales') ||
          acc?.account_name?.toLowerCase()?.includes('revenue') ||
          acc?.account_name?.toLowerCase()?.includes('income')
        );

        const selectedAccountId = preferredAccount ? preferredAccount?.id : revenueAccounts?.[0]?.id;
        console.log('INVOICE LINE ITEM FIX - Selected existing account ID:', selectedAccountId);
        return selectedAccountId;
      }

      // STEP 2: If no revenue accounts exist, try to find ANY accounts (fallback)
      console.log('INVOICE LINE ITEM FIX - No revenue accounts found, looking for any accounts...');
      const { data: anyAccounts } = await supabase?.from('chart_of_accounts')
        ?.select('id, account_name, account_type')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'active')
        ?.limit(5);

      if (anyAccounts && anyAccounts?.length > 0) {
        console.log('INVOICE LINE ITEM FIX - Found alternative accounts:', anyAccounts?.length);
        // Prefer any account type that could work for revenue
        const acceptableAccount = anyAccounts?.find(acc =>
          ['revenue', 'asset', 'equity']?.includes(acc?.account_type)
        );
        if (acceptableAccount) {
          console.log('INVOICE LINE ITEM FIX - Using alternative account ID:', acceptableAccount?.id);
          return acceptableAccount?.id;
        }
      }

      // STEP 3: Last resort - return null (will trigger account creation)
      console.log('INVOICE LINE ITEM FIX - No suitable accounts found, returning null for creation');
      return null;
    } catch (error) {
      console.error('INVOICE LINE ITEM FIX - Error getting default revenue account:', error);
      return null;
    }
  },

  async createInvoice(invoiceData, organizationId, userId) {
    try {
      const { line_items, ...invoiceFields } = invoiceData;

      console.log('INVOICE CREATION - Starting invoice creation for org:', organizationId);
      console.log('INVOICE CREATION - Line items count:', line_items?.length || 0);

      // CRITICAL FIX: Generate invoice number using the database function before inserting
      const { data: invoiceNumberData, error: numberError } = await supabase
        ?.rpc('generate_invoice_number');

      if (numberError) {
        console.error('Error generating invoice number:', numberError);
        // If function fails, generate a fallback invoice number
        const fallbackNumber = `INV-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        console.warn('Using fallback invoice number:', fallbackNumber);

        const { data: invoice, error: invoiceError } = await supabase?.from('invoices')?.insert({
          ...invoiceFields,
          invoice_number: fallbackNumber,
          organization_id: organizationId,
          created_by: userId
        })?.select()?.single();

        if (invoiceError) throw invoiceError;

        // Create line items if provided
        if (line_items && line_items?.length > 0) {
          // CRITICAL FIX: Enhanced account resolution with multiple fallbacks
          let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

          // CRITICAL FIX: If still no account found, create a guaranteed revenue account
          if (!defaultAccountId) {
            console.warn('INVOICE LINE ITEM FIX - No revenue account found, creating guaranteed default');

            // Generate unique account code to avoid conflicts
            const uniqueCode = `4000-${Date.now()?.toString()?.slice(-6)}`;
            const { data: newRevenueAccount, error: accountError } = await supabase?.from('chart_of_accounts')?.insert({
              account_name: 'Sales Revenue (Auto-Created)',
              account_code: uniqueCode,
              account_type: 'revenue',
              account_subtype: 'operating_revenue',
              normal_balance: 'credit',
              organization_id: organizationId,
              created_by: userId,
              status: 'active',
              allows_posting: true,
              account_level: 0,
              description: 'Automatically created revenue account for invoice line items'
            })?.select('id')?.single();

            if (accountError) {
              console.error('INVOICE LINE ITEM FIX - Failed to create revenue account:', accountError);
              // CRITICAL: If account creation fails, try with different approach
              const fallbackCode = `REV-${Math.random()?.toString(36)?.substr(2, 9)}`;
              const { data: fallbackAccount, error: fallbackError } = await supabase?.from('chart_of_accounts')?.insert({
                account_name: 'Revenue Account (System)',
                account_code: fallbackCode,
                account_type: 'revenue',
                account_subtype: 'operating_revenue',
                normal_balance: 'credit',
                organization_id: organizationId,
                created_by: userId,
                status: 'active'
              })?.select('id')?.single();

              if (fallbackError) {
                throw new Error('CRITICAL ERROR: Cannot create invoice line items. Failed to create required revenue account. Please create a revenue account in Chart of Accounts manually and try again.');
              }
              defaultAccountId = fallbackAccount?.id;
            } else {
              defaultAccountId = newRevenueAccount?.id;
            }
          }

          // FINAL VALIDATION: Ensure we have a valid account ID
          if (!defaultAccountId) {
            throw new Error('CRITICAL ERROR: Cannot create invoice line items without a valid account reference. Please ensure at least one account exists in your Chart of Accounts.');
          }

          console.log('INVOICE LINE ITEM FIX - Using account ID:', defaultAccountId);

          const lineItemsToInsert = line_items?.map((item, index) => ({
            invoice_id: invoice?.id,
            line_number: index + 1,
            item_id: item?.item_id || null,
            item_description: item?.item_description || 'Line Item',
            quantity: parseFloat(item?.quantity || 1),
            unit_price: parseFloat(item?.unit_price || 0),
            tax_rate: parseFloat(item?.tax_rate || 0),
            tax_amount: parseFloat(item?.tax_amount || 0),
            notes: item?.notes || null,
            weight_per_unit: parseFloat(item?.weight_per_unit || 1),
            // CRITICAL FIX: GUARANTEE account_id is never null
            account_id: item?.account_id || defaultAccountId
          }));

          // CRITICAL FIX: Triple validation that NO line items have null account_id
          const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id || item?.account_id === null || item?.account_id === undefined);
          if (invalidItems?.length > 0) {
            console.error('INVOICE LINE ITEM FIX - Found invalid items:', invalidItems);
            throw new Error(`CRITICAL ERROR: Cannot create invoice line items. ${invalidItems?.length} line item(s) still have null account_id after all fallbacks. This is a system error.`);
          }

          console.log('INVOICE LINE ITEM FIX - Inserting line items:', lineItemsToInsert?.length);
          const { error: lineItemsError } = await supabase?.from('invoice_line_items')?.insert(lineItemsToInsert);

          if (lineItemsError) {
            console.error('INVOICE LINE ITEM FIX - Line items insertion error:', lineItemsError);
            // CRITICAL FIX: Provide more specific error handling for account_id constraints
            if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
              throw new Error(`INVOICE LINE ITEM ERROR: Account ID constraint violation. This should not happen after all validations. Error: ${lineItemsError?.message}`);
            }
            throw lineItemsError;
          }

          console.log('INVOICE LINE ITEM FIX - Successfully created line items');
        }

        return { data: invoice, error: null };
      }

      let invoiceNumber = invoiceNumberData;

      // Start transaction - Create invoice with the generated invoice number
      const { data: invoice, error: invoiceError } = await supabase?.from('invoices')?.insert({
        ...invoiceFields,
        invoice_number: invoiceNumber, // CRITICAL FIX: Add the required invoice_number field
        organization_id: organizationId,
        created_by: userId
      })?.select()?.single();

      if (invoiceError) {
        // If primary insert fails due to invoice_number constraint, try with fallback
        if (invoiceError?.code === '23502' && invoiceError?.message?.includes('invoice_number')) {
          console.warn('Invoice number constraint error, retrying with fallback');
          const fallbackNumber = `INV-${new Date()?.getFullYear()}-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 5)}`;

          const { data: retryData, error: retryError } = await supabase?.from('invoices')?.insert({
            ...invoiceFields,
            invoice_number: fallbackNumber,
            organization_id: organizationId,
            created_by: userId
          })?.select()?.single();

          if (retryError) throw retryError;

          // Create line items for retry data if provided (same enhanced logic)
          if (line_items && line_items?.length > 0) {
            let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

            if (!defaultAccountId) {
              console.warn('INVOICE LINE ITEM FIX - Creating account for retry scenario');
              const uniqueCode = `4000-${Date.now()?.toString()?.slice(-6)}`;
              const { data: newRevenueAccount, error: accountError } = await supabase?.from('chart_of_accounts')?.insert({
                account_name: 'Sales Revenue (Auto-Created)',
                account_code: uniqueCode,
                account_type: 'revenue',
                account_subtype: 'operating_revenue',
                normal_balance: 'credit',
                organization_id: organizationId,
                created_by: userId,
                status: 'active',
                allows_posting: true
              })?.select('id')?.single();

              if (accountError) {
                throw new Error('Cannot create invoice line items: Failed to create required revenue account in retry scenario.');
              }
              defaultAccountId = newRevenueAccount?.id;
            }

            const lineItemsToInsert = line_items?.map((item, index) => ({
              invoice_id: retryData?.id,
              line_number: index + 1,
              item_id: item?.item_id || null,
              item_description: item?.item_description || 'Line Item',
              quantity: parseFloat(item?.quantity || 1),
              unit_price: parseFloat(item?.unit_price || 0),
              tax_rate: parseFloat(item?.tax_rate || 0),
              tax_amount: parseFloat(item?.tax_amount || 0),
              notes: item?.notes || null,
              weight_per_unit: parseFloat(item?.weight_per_unit || 1),
              account_id: item?.account_id || defaultAccountId
            }));

            const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id);
            if (invalidItems?.length > 0) {
              throw new Error(`Cannot create invoice line items in retry: ${invalidItems?.length} line item(s) have null account_id.`);
            }

            const { error: lineItemsError } = await supabase?.from('invoice_line_items')?.insert(lineItemsToInsert);
            if (lineItemsError) {
              if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
                throw new Error('Failed to create invoice line items in retry: Account ID constraint violation.');
              }
              throw lineItemsError;
            }
          }

          return { data: retryData, error: null };
        }
        throw invoiceError;
      }

      // Create line items if provided (same enhanced logic for main path)
      if (line_items && line_items?.length > 0) {
        let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

        if (!defaultAccountId) {
          console.warn('INVOICE LINE ITEM FIX - Creating account for main path');
          const uniqueCode = `4000-${Date.now()?.toString()?.slice(-6)}`;
          const { data: newRevenueAccount, error: accountError } = await supabase?.from('chart_of_accounts')?.insert({
            account_name: 'Sales Revenue (Auto-Created)',
            account_code: uniqueCode,
            account_type: 'revenue',
            account_subtype: 'operating_revenue',
            normal_balance: 'credit',
            organization_id: organizationId,
            created_by: userId,
            status: 'active',
            allows_posting: true,
            account_level: 0,
            description: 'Automatically created revenue account for invoice line items'
          })?.select('id')?.single();

          if (accountError) {
            throw new Error('Cannot create invoice line items: Failed to create required revenue account.');
          }
          defaultAccountId = newRevenueAccount?.id;
        }

        const lineItemsToInsert = line_items?.map((item, index) => ({
          invoice_id: invoice?.id,
          line_number: index + 1,
          item_id: item?.item_id || null,
          item_description: item?.item_description || 'Line Item',
          quantity: parseFloat(item?.quantity || 1),
          unit_price: parseFloat(item?.unit_price || 0),
          tax_rate: parseFloat(item?.tax_rate || 0),
          tax_amount: parseFloat(item?.tax_amount || 0),
          notes: item?.notes || null,
          weight_per_unit: parseFloat(item?.weight_per_unit || 1),
          account_id: item?.account_id || defaultAccountId
        }));

        const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id);
        if (invalidItems?.length > 0) {
          throw new Error(`Cannot create invoice line items: ${invalidItems?.length} line item(s) have null account_id.`);
        }

        console.log('INVOICE LINE ITEM FIX - Final insert for main path');
        const { error: lineItemsError } = await supabase?.from('invoice_line_items')?.insert(lineItemsToInsert);

        if (lineItemsError) {
          console.error('Line items insertion error:', lineItemsError);
          if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
            throw new Error('Failed to create invoice line items: Account ID constraint violation after all validations.');
          }
          throw lineItemsError;
        }
      }

      return { data: invoice, error: null };
    } catch (error) {
      console.error('Error creating invoice:', error);
      return { data: null, error };
    }
  },

  async updateInvoice(invoiceId, invoiceData, organizationId) {
    try {
      const { line_items, ...invoiceFields } = invoiceData;

      // CRITICAL FIX: Enhanced UUID validation to prevent "undefined" errors
      if (!invoiceId || invoiceId === 'undefined' || invoiceId === null || invoiceId === '' || invoiceId === undefined) {
        console.error('Invalid invoice ID provided:', invoiceId);
        return {
          data: null,
          error: {
            message: 'Invalid invoice ID provided. Cannot update invoice without a valid ID.',
            code: 'INVALID_INVOICE_ID',
            details: 'Invoice ID must be a valid UUID, but received: ' + invoiceId
          }
        };
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(invoiceId)) {
        console.error('Invalid UUID format for invoice ID:', invoiceId);
        return {
          data: null,
          error: {
            message: 'Invalid invoice ID format. Must be a valid UUID.',
            code: 'INVALID_UUID_FORMAT',
            details: 'Invoice ID must be a valid UUID format, but received: ' + invoiceId
          }
        };
      }

      // CRITICAL FIX: Enhanced customer field processing with multiple validation layers
      if (invoiceFields?.customer && !invoiceFields?.customer_id) {
        // If customer object is provided, extract the ID with validation
        if (typeof invoiceFields?.customer === 'object') {
          invoiceFields.customer_id = invoiceFields?.customer?.id;
        } else if (typeof invoiceFields?.customer === 'string') {
          // Handle case where customer might be a UUID string
          invoiceFields.customer_id = invoiceFields?.customer;
        }
        delete invoiceFields?.customer; // Remove the customer object to prevent schema errors
      }

      // CRITICAL FIX: Remove any fields that might cause PGRST204 schema cache errors
      const cleanedInvoiceFields = { ...invoiceFields };
      delete cleanedInvoiceFields?.customer; // FIXED: Remove customer object (not a column)
      delete cleanedInvoiceFields?.invoice_line_items; // Remove line items (handled separately)
      delete cleanedInvoiceFields?.balance_due; // Remove auto-generated column

      // Remove system fields that shouldn't be updated
      delete cleanedInvoiceFields?.id; // Don't update primary key
      delete cleanedInvoiceFields?.created_at; // Don't update creation timestamp
      delete cleanedInvoiceFields?.organization_id; // Don't update organization ID
      delete cleanedInvoiceFields?.created_by; // Don't update creator ID
      delete cleanedInvoiceFields?.invoice_number; // Don't update invoice number (immutable)

      // Add proper timestamp for update
      cleanedInvoiceFields.updated_at = new Date()?.toISOString();

      console.log('INVOICE UPDATE - Invoice ID:', invoiceId);
      console.log('INVOICE UPDATE - Organization ID:', organizationId);
      console.log('INVOICE UPDATE - Original data:', invoiceData);
      console.log('INVOICE UPDATE - Cleaned fields:', cleanedInvoiceFields);

      // Update invoice using correct column names with explicit customer_id reference
      const { data: invoice, error: invoiceError } = await supabase
        ?.from('invoices')
        ?.update(cleanedInvoiceFields)
        ?.eq('id', invoiceId)
        ?.eq('organization_id', organizationId)
        ?.select('*')
        ?.single();

      if (invoiceError) {
        // CRITICAL FIX: Handle 22P02 UUID errors specifically with enhanced error detection
        if (invoiceError?.code === '22P02' && invoiceError?.message?.includes('invalid input syntax for type uuid')) {
          console.error('UUID validation error - invalid invoice ID format:', invoiceError);
          return {
            data: null,
            error: {
              message: 'Invalid UUID format provided for invoice ID. The ID must be a valid UUID, not "undefined" or empty string.',
              code: 'UUID_VALIDATION_ERROR',
              details: 'The invoice ID must be a valid UUID format. Please ensure the ID is properly formatted and not "undefined". Received ID: ' + invoiceId,
              originalError: invoiceError
            }
          };
        }

        // CRITICAL FIX: Handle PGRST204 schema cache errors specifically with retry mechanism
        if (invoiceError?.code === 'PGRST204') {
          console.error('Schema cache error in updateInvoice - column not found:', invoiceError);

          // ENHANCED RETRY: Try with minimal update to avoid schema cache issues
          const minimalFields = {
            // Only include essential fields that definitely exist
            ...(cleanedInvoiceFields?.customer_id && { customer_id: cleanedInvoiceFields?.customer_id }),
            ...(cleanedInvoiceFields?.status && { status: cleanedInvoiceFields?.status }),
            ...(cleanedInvoiceFields?.total_amount && { total_amount: cleanedInvoiceFields?.total_amount }),
            ...(cleanedInvoiceFields?.amount_paid && { amount_paid: cleanedInvoiceFields?.amount_paid }),
            ...(cleanedInvoiceFields?.due_date && { due_date: cleanedInvoiceFields?.due_date }),
            updated_at: new Date()?.toISOString()
          };

          console.log('INVOICE UPDATE RETRY - Using minimal fields:', minimalFields);

          const { data: retryInvoice, error: retryError } = await supabase
            ?.from('invoices')
            ?.update(minimalFields)
            ?.eq('id', invoiceId)
            ?.eq('organization_id', organizationId)
            ?.select('*')
            ?.single();

          if (retryError) {
            console.error('Retry also failed:', retryError);
            return {
              data: null,
              error: {
                message: 'Invoice update failed due to persistent schema cache error. The referenced column may not exist in the invoices table.',
                code: 'SCHEMA_CACHE_ERROR',
                details: 'Both primary and retry attempts failed. Please ensure you are using customer_id (not customer) for customer references.',
                originalError: invoiceError,
                retryError: retryError
              }
            };
          }

          // If retry succeeded, continue with line items processing
          console.log('INVOICE UPDATE RETRY - Success, proceeding with line items');

          // Update line items if provided (same logic as main path)
          if (line_items && line_items?.length > 0) {
            await this.updateInvoiceLineItems(invoiceId, line_items, organizationId);
          }

          return {
            data: retryInvoice,
            error: {
              type: 'schema_cache_warning',
              message: 'Invoice updated successfully using retry mechanism due to schema cache error',
              originalError: invoiceError
            }
          };
        }
        throw invoiceError;
      }

      // Update line items if provided (existing logic remains the same)
      if (line_items && line_items?.length > 0) {
        await this.updateInvoiceLineItems(invoiceId, line_items, organizationId);
      }

      return { data: invoice, error: null };
    } catch (error) {
      console.error('Error updating invoice:', error);

      // CRITICAL: Handle 22P02 UUID errors with specific messaging for "undefined" string
      if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
        return {
          data: null,
          error: {
            message: 'Invalid UUID format provided for invoice ID. The ID must be a valid UUID, not "undefined" or empty string.',
            code: 'UUID_VALIDATION_ERROR',
            details: 'The invoice ID must be a valid UUID format. Please ensure the ID is properly formatted and not "undefined". This usually indicates that the invoice ID was not properly passed from the frontend component.'
          }
        };
      }

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Invoice update failed due to schema cache error. The referenced column may not exist in the invoices table.',
            code: 'SCHEMA_CACHE_ERROR',
            details: 'Please ensure you are using customer_id (not customer) for customer references. If the issue persists, the database schema cache may need to be refreshed.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // CRITICAL FIX: Enhanced helper method for line items updates with better error handling
  async updateInvoiceLineItems(invoiceId, line_items, organizationId) {
    try {
      // Delete existing line items
      const { error: deleteError } = await supabase?.from('invoice_line_items')?.delete()?.eq('invoice_id', invoiceId);

      if (deleteError) throw deleteError;

      // Get default revenue account for line items without account_id
      let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

      if (!defaultAccountId) {
        throw new Error('Cannot update invoice line items: No revenue account found for organization. Please ensure at least one revenue account exists in the chart of accounts.');
      }

      // Insert updated line items
      const lineItemsToInsert = line_items?.map((item, index) => ({
        invoice_id: invoiceId,
        line_number: index + 1,
        item_id: item?.item_id || null,
        item_description: item?.item_description,
        quantity: parseFloat(item?.quantity || 1),
        unit_price: parseFloat(item?.unit_price || 0),
        tax_rate: parseFloat(item?.tax_rate || 0),
        tax_amount: parseFloat(item?.tax_amount || 0),
        notes: item?.notes || null,
        weight_per_unit: parseFloat(item?.weight_per_unit || 1),
        account_id: item?.account_id || defaultAccountId
      }));

      const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id);
      if (invalidItems?.length > 0) {
        throw new Error(`Cannot update invoice line items: Missing account_id for ${invalidItems?.length} line item(s). All line items must have a valid account reference.`);
      }

      const { error: lineItemsError } = await supabase?.from('invoice_line_items')?.insert(lineItemsToInsert);

      if (lineItemsError) {
        console.error('Line items insertion error:', lineItemsError);
        if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
          throw new Error('Failed to update invoice line items: All line items must have a valid account reference. Please ensure revenue accounts exist in your Chart of Accounts.');
        }
        throw lineItemsError;
      }

      console.log('INVOICE LINE ITEMS UPDATE - Successfully updated line items');
      return true;
    } catch (error) {
      console.error('Error updating invoice line items:', error);
      throw error;
    }
  },

  // Delete invoice with constraint checking
  async deleteInvoice(invoiceId, organizationId) {
    try {
      // Check for related records
      const constraintChecks = await Promise.all([
        // Check payments received
        supabase?.from('payments_received')?.select('id')?.eq('reference_invoice_id', invoiceId)?.limit(1),
        // Check credit notes
        supabase?.from('credit_notes')?.select('id')?.eq('reference_invoice_id', invoiceId)?.limit(1),
        // Check delivery challans
        supabase?.from('delivery_challans')?.select('id')?.eq('reference_invoice_id', invoiceId)?.limit(1),
        // Check payment links
        supabase?.from('payment_links')?.select('id')?.eq('reference_invoice_id', invoiceId)?.limit(1),
        // Check quote conversions
        supabase?.from('quotes')?.select('id')?.eq('converted_to_invoice_id', invoiceId)?.limit(1),
        // Check retainer utilizations
        supabase?.from('retainer_utilizations')?.select('id')?.eq('reference_invoice_id', invoiceId)?.limit(1)
      ]);

      const constraints = [];
      const [payments, creditNotes, deliveryChallans, paymentLinks, quotes, retainerUtils] = constraintChecks;

      if (payments?.data?.length > 0) constraints?.push('payments received');
      if (creditNotes?.data?.length > 0) constraints?.push('credit notes');
      if (deliveryChallans?.data?.length > 0) constraints?.push('delivery challans');
      if (paymentLinks?.data?.length > 0) constraints?.push('payment links');
      if (quotes?.data?.length > 0) constraints?.push('converted quotes');
      if (retainerUtils?.data?.length > 0) constraints?.push('retainer utilizations');

      if (constraints?.length > 0) {
        return {
          data: null,
          error: {
            type: 'constraint_violation',
            message: `Cannot delete invoice. Has related ${constraints?.join(', ')}.`,
            constraints
          }
        };
      }

      const { error } = await supabase?.from('invoices')?.delete()?.eq('id', invoiceId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting invoice:', error);
      return { data: null, error };
    }
  },

  // Force delete invoice (cascades to all related records)
  async forceDeleteInvoice(invoiceId, organizationId) {
    try {
      const { error } = await supabase?.from('invoices')?.delete()?.eq('id', invoiceId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true, cascaded: true }, error: null };
    } catch (error) {
      console.error('Error force deleting invoice:', error);
      return { data: null, error };
    }
  },

  // Convert quote to invoice
  async convertQuoteToInvoice(quoteId, invoiceData, organizationId, userId) {
    try {
      // Get quote details including line items
      const { data: quote, error: quoteError } = await supabase?.from('quotes')?.select(`
          *,
          quote_line_items(*)
        `)?.eq('id', quoteId)?.eq('organization_id', organizationId)?.single();

      if (quoteError) throw quoteError;

      // CRITICAL FIX: Generate invoice number using the database function before creating invoice
      const { data: invoiceNumberData, error: numberError } = await supabase
        ?.rpc('generate_invoice_number');

      let invoiceNumber;
      if (numberError) {
        console.error('Error generating invoice number:', numberError);
        // If function fails, generate a fallback invoice number
        invoiceNumber = `INV-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        console.warn('Using fallback invoice number:', invoiceNumber);
      } else {
        invoiceNumber = invoiceNumberData;
      }

      // Create invoice from quote
      const { data: invoice, error: invoiceError } = await supabase?.from('invoices')?.insert({
        ...invoiceData,
        invoice_number: invoiceNumber, // CRITICAL FIX: Add the required invoice_number field
        customer_id: quote?.customer_id,
        subtotal: quote?.subtotal,
        tax_amount: quote?.tax_amount,
        total_amount: quote?.total_amount,
        currency_code: quote?.currency_code,
        organization_id: organizationId,
        created_by: userId
      })?.select()?.single();

      if (invoiceError) {
        // If primary insert fails due to invoice_number constraint, try with fallback
        if (invoiceError?.code === '23502' && invoiceError?.message?.includes('invoice_number')) {
          console.warn('Invoice number constraint error during conversion, retrying with fallback');
          const fallbackNumber = `INV-${new Date()?.getFullYear()}-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 5)}`;

          const { data: retryData, error: retryError } = await supabase?.from('invoices')?.insert({
            ...invoiceData,
            invoice_number: fallbackNumber,
            customer_id: quote?.customer_id,
            subtotal: quote?.subtotal,
            tax_amount: quote?.tax_amount,
            total_amount: quote?.total_amount,
            currency_code: quote?.currency_code,
            organization_id: organizationId,
            created_by: userId
          })?.select()?.single();

          if (retryError) throw retryError;

          // Continue with line items processing using retry data
          const processedInvoice = retryData;

          // Copy line items from quote to invoice
          if (quote?.quote_line_items && quote?.quote_line_items?.length > 0) {
            const invoiceLineItems = quote?.quote_line_items?.map(item => ({
              invoice_id: processedInvoice?.id,
              line_number: item?.line_number,
              item_id: item?.item_id,
              item_description: item?.item_description,
              quantity: item?.quantity,
              unit_price: item?.unit_price,
              tax_rate: item?.tax_rate,
              tax_amount: item?.tax_amount,
              notes: item?.notes,
              weight_per_unit: item?.weight_per_unit,
              account_id: item?.account_id
            }));

            const { error: lineItemsError } = await supabase?.from('invoice_line_items')?.insert(invoiceLineItems);

            if (lineItemsError) throw lineItemsError;
          }

          // Update quote status to converted
          const { error: updateQuoteError } = await supabase?.from('quotes')?.update({
            status: 'converted',
            converted_to_invoice_id: processedInvoice?.id
          })?.eq('id', quoteId);

          if (updateQuoteError) throw updateQuoteError;

          return { data: processedInvoice, error: null };
        }
        throw invoiceError;
      }

      // Copy line items from quote to invoice
      if (quote?.quote_line_items && quote?.quote_line_items?.length > 0) {
        const invoiceLineItems = quote?.quote_line_items?.map(item => ({
          invoice_id: invoice?.id,
          line_number: item?.line_number,
          item_id: item?.item_id,
          item_description: item?.item_description,
          quantity: item?.quantity,
          unit_price: item?.unit_price,
          tax_rate: item?.tax_rate,
          tax_amount: item?.tax_amount,
          notes: item?.notes,
          weight_per_unit: item?.weight_per_unit,
          account_id: item?.account_id
        }));

        const { error: lineItemsError } = await supabase?.from('invoice_line_items')?.insert(invoiceLineItems);

        if (lineItemsError) throw lineItemsError;
      }

      // Update quote status to converted
      const { error: updateQuoteError } = await supabase?.from('quotes')?.update({
        status: 'converted',
        converted_to_invoice_id: invoice?.id
      })?.eq('id', quoteId);

      if (updateQuoteError) throw updateQuoteError;

      return { data: invoice, error: null };
    } catch (error) {
      console.error('Error converting quote to invoice:', error);
      return { data: null, error };
    }
  }
};

// Enhanced Quote Service with Line Items Support
export const quoteService = {
  async getQuotes(organizationId, filters = {}) {
    try {
      let query = supabase?.from('quotes')?.select(`
          *,
          customer:customers(*),
          quote_line_items(
            *,
            item:items(*),
            account:chart_of_accounts(*)
          )
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.customer_id) {
        query = query?.eq('customer_id', filters?.customer_id);
      }
      if (filters?.dateFrom) {
        query = query?.gte('issue_date', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('issue_date', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching quotes:', error);
      return { data: [], error };
    }
  },

  async getQuoteDetails(quoteId, organizationId) {
    try {
      const { data, error } = await supabase?.from('quotes')?.select(`
          *,
          customer:customers(*),
          quote_line_items(
            *,
            item:items(*),
            account:chart_of_accounts(*)
          )
        `)?.eq('id', quoteId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching quote details:', error);
      return { data: null, error };
    }
  },

  // CRITICAL FIX: Helper function to get default revenue account for line items
  async getDefaultRevenueAccount(organizationId) {
    try {
      // Look for a default revenue account (preferably "Sales Revenue" or similar)
      const { data: revenueAccounts, error } = await supabase?.from('chart_of_accounts')
        ?.select('id, account_name, account_code')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'revenue')
        ?.eq('status', 'active')
        ?.order('account_code', { ascending: true })
        ?.limit(5);

      if (error) {
        console.error('Error fetching revenue accounts:', error);
        return null;
      }

      if (revenueAccounts && revenueAccounts?.length > 0) {
        // Prefer accounts with "Sales", "Revenue", or "Income" in the name
        const preferredAccount = revenueAccounts?.find(acc =>
          acc?.account_name?.toLowerCase()?.includes('sales') ||
          acc?.account_name?.toLowerCase()?.includes('revenue') ||
          acc?.account_name?.toLowerCase()?.includes('income')
        );

        return preferredAccount ? preferredAccount?.id : revenueAccounts?.[0]?.id;
      }

      // Fallback: Look for any revenue account if no specific ones found
      const { data: anyRevenueAccount } = await supabase?.from('chart_of_accounts')
        ?.select('id')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'revenue')
        ?.limit(1)
        ?.single();

      return anyRevenueAccount?.id || null;
    } catch (error) {
      console.error('Error getting default revenue account:', error);
      return null;
    }
  },

  async createQuote(quoteData, organizationId, userId) {
    try {
      const { line_items, ...quoteFields } = quoteData;

      // CRITICAL FIX: Generate quote number using the database function before inserting
      const { data: quoteNumberData, error: numberError } = await supabase
        ?.rpc('generate_quote_number', { org_id: organizationId });

      if (numberError) {
        console.error('Error generating quote number:', numberError);
        // If function fails, generate a fallback quote number
        const fallbackNumber = `QUO-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        console.warn('Using fallback quote number:', fallbackNumber);

        const { data: quote, error: quoteError } = await supabase?.from('quotes')?.insert({
          ...quoteFields,
          quote_number: fallbackNumber,
          organization_id: organizationId,
          created_by: userId
        })?.select()?.single();

        if (quoteError) throw quoteError;

        // Create line items if provided
        if (line_items && line_items?.length > 0) {
          // CRITICAL FIX: Get default revenue account for line items without account_id
          let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

          if (!defaultAccountId) {
            // CRITICAL FIX: If no revenue account exists, create a basic one
            console.warn('No revenue account found, creating default revenue account');
            const { data: newRevenueAccount, error: accountError } = await supabase?.from('chart_of_accounts')?.insert({
              account_name: 'Sales Revenue',
              account_code: '4000',
              account_type: 'revenue',
              account_subtype: 'operating_revenue',
              normal_balance: 'credit',
              organization_id: organizationId,
              created_by: userId,
              status: 'active'
            })?.select('id')?.single();

            if (accountError) {
              console.error('Failed to create default revenue account:', accountError);
              throw new Error('Cannot create quote line items: No revenue account available and failed to create default account. Please create a revenue account in Chart of Accounts first.');
            }

            defaultAccountId = newRevenueAccount?.id;
          }

          const lineItemsToInsert = line_items?.map((item, index) => ({
            quote_id: quote?.id,
            line_number: index + 1,
            item_id: item?.item_id || null,
            item_description: item?.item_description,
            quantity: parseFloat(item?.quantity || 1),
            unit_price: parseFloat(item?.unit_price || 0),
            tax_rate: parseFloat(item?.tax_rate || 0),
            tax_amount: parseFloat(item?.tax_amount || 0),
            notes: item?.notes || null,
            weight_per_unit: parseFloat(item?.weight_per_unit || 1),
            // CRITICAL FIX: Ensure account_id is always provided (never null)
            account_id: item?.account_id || defaultAccountId // Use default revenue account if none provided
          }));

          // CRITICAL FIX: Final validation that all line items have account_id before inserting
          const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id);
          if (invalidItems?.length > 0) {
            throw new Error(`Cannot create quote line items: Missing account_id for ${invalidItems?.length} line item(s). All line items must have a valid account reference.`);
          }

          const { error: lineItemsError } = await supabase?.from('quote_line_items')?.insert(lineItemsToInsert);

          if (lineItemsError) {
            console.error('Line items insertion error:', lineItemsError);
            // CRITICAL FIX: Provide more specific error handling for account_id constraints
            if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
              throw new Error('Failed to create quote line items: All line items must have a valid account reference. Please ensure revenue accounts exist in your Chart of Accounts.');
            }
            throw lineItemsError;
          }
        }

        return { data: quote, error: null };
      }

      const quoteNumber = quoteNumberData;

      // Start transaction - Create quote with the generated quote number
      const { data: quote, error: quoteError } = await supabase?.from('quotes')?.insert({
        ...quoteFields,
        quote_number: quoteNumber, // CRITICAL FIX: Add the required quote_number field
        organization_id: organizationId,
        created_by: userId
      })?.select()?.single();

      if (quoteError) {
        // If primary insert fails due to quote_number constraint, try with fallback
        if (quoteError?.code === '23502' && quoteError?.message?.includes('quote_number')) {
          console.warn('Quote number constraint error, retrying with fallback');
          const fallbackNumber = `QUO-${new Date()?.getFullYear()}-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 5)}`;

          const { data: retryData, error: retryError } = await supabase?.from('quotes')?.insert({
            ...quoteFields,
            quote_number: fallbackNumber,
            organization_id: organizationId,
            created_by: userId
          })?.select()?.single();

          if (retryError) throw retryError;

          // Create line items for retry data if provided
          if (line_items && line_items?.length > 0) {
            // CRITICAL FIX: Get default revenue account for line items without account_id
            let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

            if (!defaultAccountId) {
              // CRITICAL FIX: If no revenue account exists, create a basic one
              console.warn('No revenue account found, creating default revenue account');
              const { data: newRevenueAccount, error: accountError } = await supabase?.from('chart_of_accounts')?.insert({
                account_name: 'Sales Revenue',
                account_code: '4000',
                account_type: 'revenue',
                account_subtype: 'operating_revenue',
                normal_balance: 'credit',
                organization_id: organizationId,
                created_by: userId,
                status: 'active'
              })?.select('id')?.single();

              if (accountError) {
                console.error('Failed to create default revenue account:', accountError);
                throw new Error('Cannot create quote line items: No revenue account available and failed to create default account. Please create a revenue account in Chart of Accounts first.');
              }

              defaultAccountId = newRevenueAccount?.id;
            }

            const lineItemsToInsert = line_items?.map((item, index) => ({
              quote_id: retryData?.id,
              line_number: index + 1,
              item_id: item?.item_id || null,
              item_description: item?.item_description,
              quantity: parseFloat(item?.quantity || 1),
              unit_price: parseFloat(item?.unit_price || 0),
              tax_rate: parseFloat(item?.tax_rate || 0),
              tax_amount: parseFloat(item?.tax_amount || 0),
              notes: item?.notes || null,
              weight_per_unit: parseFloat(item?.weight_per_unit || 1),
              // CRITICAL FIX: Ensure account_id is always provided (never null)
              account_id: item?.account_id || defaultAccountId // Use default revenue account if none provided
            }));

            // CRITICAL FIX: Final validation that all line items have account_id before inserting
            const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id);
            if (invalidItems?.length > 0) {
              throw new Error(`Cannot create quote line items: Missing account_id for ${invalidItems?.length} line item(s). All line items must have a valid account reference.`);
            }

            const { error: lineItemsError } = await supabase?.from('quote_line_items')?.insert(lineItemsToInsert);

            if (lineItemsError) {
              console.error('Line items insertion error:', lineItemsError);
              // CRITICAL FIX: Provide more specific error handling for account_id constraints
              if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
                throw new Error('Failed to create quote line items: All line items must have a valid account reference. Please ensure revenue accounts exist in your Chart of Accounts.');
              }
              throw lineItemsError;
            }
          }

          return { data: retryData, error: null };
        }
        throw quoteError;
      }

      // Create line items if provided
      if (line_items && line_items?.length > 0) {
        // CRITICAL FIX: Get default revenue account for line items without account_id
        let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

        if (!defaultAccountId) {
          // CRITICAL FIX: If no revenue account exists, create a basic one
          console.warn('No revenue account found, creating default revenue account');
          const { data: newRevenueAccount, error: accountError } = await supabase?.from('chart_of_accounts')?.insert({
            account_name: 'Sales Revenue',
            account_code: '4000',
            account_type: 'revenue',
            account_subtype: 'operating_revenue',
            normal_balance: 'credit',
            organization_id: organizationId,
            created_by: userId,
            status: 'active'
          })?.select('id')?.single();

          if (accountError) {
            console.error('Failed to create default revenue account:', accountError);
            throw new Error('Cannot create quote line items: No revenue account available and failed to create default account. Please create a revenue account in Chart of Accounts first.');
          }

          defaultAccountId = newRevenueAccount?.id;
        }

        const lineItemsToInsert = line_items?.map((item, index) => ({
          quote_id: quote?.id,
          line_number: index + 1,
          item_id: item?.item_id || null,
          item_description: item?.item_description,
          quantity: parseFloat(item?.quantity || 1),
          unit_price: parseFloat(item?.unit_price || 0),
          tax_rate: parseFloat(item?.tax_rate || 0),
          tax_amount: parseFloat(item?.tax_amount || 0),
          notes: item?.notes || null,
          weight_per_unit: parseFloat(item?.weight_per_unit || 1),
          // CRITICAL FIX: Ensure account_id is always provided (never null)
          account_id: item?.account_id || defaultAccountId // Use default revenue account if none provided
        }));

        // CRITICAL FIX: Final validation that all line items have account_id before inserting
        const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id);
        if (invalidItems?.length > 0) {
          throw new Error(`Cannot create quote line items: Missing account_id for ${invalidItems?.length} line item(s). All line items must have a valid account reference.`);
        }

        const { error: lineItemsError } = await supabase?.from('quote_line_items')?.insert(lineItemsToInsert);

        if (lineItemsError) {
          console.error('Line items insertion error:', lineItemsError);
          // CRITICAL FIX: Provide more specific error handling for account_id constraints
          if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
            throw new Error('Failed to create quote line items: All line items must have a valid account reference. Please ensure revenue accounts exist in your Chart of Accounts.');
          }
          throw lineItemsError;
        }
      }

      return { data: quote, error: null };
    } catch (error) {
      console.error('Error creating quote:', error);
      return { data: null, error };
    }
  },

  async updateQuote(quoteId, quoteData, organizationId) {
    try {
      const { line_items, ...quoteFields } = quoteData;

      // CRITICAL FIX: Enhanced UUID validation to prevent "undefined" errors
      if (!quoteId || quoteId === 'undefined' || quoteId === null || quoteId === '' || quoteId === undefined) {
        console.error('Invalid quote ID provided:', quoteId);
        return {
          data: null,
          error: {
            message: 'Invalid quote ID provided. Cannot update quote without a valid ID.',
            code: 'INVALID_QUOTE_ID',
            details: 'Quote ID must be a valid UUID, but received: ' + quoteId
          }
        };
      }

      // Validate UUID format for quote ID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(quoteId)) {
        console.error('Invalid UUID format for quote ID:', quoteId);
        return {
          data: null,
          error: {
            message: 'Invalid quote ID format. Must be a valid UUID.',
            code: 'INVALID_UUID_FORMAT',
            details: 'Quote ID must be a valid UUID format, but received: ' + quoteId
          }
        };
      }

      // CRITICAL FIX: Enhanced customer field processing with multiple validation layers
      if (quoteFields?.customer && !quoteFields?.customer_id) {
        // If customer object is provided, extract the ID with validation
        if (typeof quoteFields?.customer === 'object') {
          quoteFields.customer_id = quoteFields?.customer?.id;
        } else if (typeof quoteFields?.customer === 'string') {
          // Handle case where customer might be a UUID string
          quoteFields.customer_id = quoteFields?.customer;
        }
        delete quoteFields?.customer; // Remove the customer object to prevent schema errors
      }

      // CRITICAL FIX: Enhanced customer_id field processing with UUID validation
      if (quoteFields?.customer_id !== undefined) {
        // Validate customer_id if it's provided
        if (quoteFields?.customer_id === 'undefined' || quoteFields?.customer_id === '' || quoteFields?.customer_id === null) {
          console.error('Invalid customer_id provided:', quoteFields?.customer_id);
          return {
            data: null,
            error: {
              message: 'Invalid customer ID provided. Customer ID must be a valid UUID or null.',
              code: 'INVALID_CUSTOMER_ID',
              details: 'Customer ID must be a valid UUID format, but received: ' + quoteFields?.customer_id
            }
          };
        }

        // Validate UUID format for customer_id if it's not null
        if (quoteFields?.customer_id && !uuidRegex?.test(quoteFields?.customer_id)) {
          console.error('Invalid UUID format for customer_id:', quoteFields?.customer_id);
          return {
            data: null,
            error: {
              message: 'Invalid customer ID format. Must be a valid UUID.',
              code: 'INVALID_CUSTOMER_UUID_FORMAT',
              details: 'Customer ID must be a valid UUID format, but received: ' + quoteFields?.customer_id
            }
          };
        }
      }

      // CRITICAL FIX: Remove any fields that might cause PGRST204 schema cache errors
      const cleanedQuoteFields = { ...quoteFields };

      // Remove profile data that causes PGRST204 error - these don't exist as columns
      delete cleanedQuoteFields?.customer; // FIXED: Remove customer object (not a column)
      delete cleanedQuoteFields?.quote_line_items; // Remove line items (handled separately)
      delete cleanedQuoteFields?.converted_to_invoice_id; // Remove conversion field if present

      // Remove system fields that shouldn't be updated
      delete cleanedQuoteFields?.id; // Don't update primary key
      delete cleanedQuoteFields?.created_at; // Don't update creation timestamp
      delete cleanedQuoteFields?.organization_id; // Don't update organization ID
      delete cleanedQuoteFields?.created_by; // Don't update creator ID
      delete cleanedQuoteFields?.quote_number; // Don't update quote number (immutable)

      // Add proper timestamp for update
      cleanedQuoteFields.updated_at = new Date()?.toISOString();

      console.log('QUOTE UPDATE - Quote ID:', quoteId);
      console.log('QUOTE UPDATE - Organization ID:', organizationId);
      console.log('QUOTE UPDATE - Original data:', quoteData);
      console.log('QUOTE UPDATE - Cleaned fields:', cleanedQuoteFields);

      // Update quote using correct column names with explicit customer_id reference
      const { data: quote, error: quoteError } = await supabase
        ?.from('quotes')
        ?.update(cleanedQuoteFields)
        ?.eq('id', quoteId)
        ?.eq('organization_id', organizationId)
        ?.select('*')
        ?.single();

      if (quoteError) {
        // CRITICAL FIX: Handle 22P02 UUID errors specifically with enhanced error detection
        if (quoteError?.code === '22P02' && quoteError?.message?.includes('invalid input syntax for type uuid')) {
          console.error('UUID validation error - invalid quote ID format:', quoteError);
          return {
            data: null,
            error: {
              message: 'Invalid UUID format provided for quote ID. The ID must be a valid UUID, not "undefined" or empty string.',
              code: 'UUID_VALIDATION_ERROR',
              details: 'The quote ID must be a valid UUID format. Please ensure the ID is properly formatted and not "undefined". Received ID: ' + quoteId,
              originalError: quoteError
            }
          };
        }

        // CRITICAL FIX: Handle PGRST204 schema cache errors specifically with retry mechanism
        if (quoteError?.code === 'PGRST204') {
          console.error('Schema cache error in updateQuote - column not found:', quoteError);

          // ENHANCED RETRY: Try with minimal update to avoid schema cache issues
          const minimalFields = {
            // Only include essential fields that definitely exist
            ...(cleanedQuoteFields?.customer_id && { customer_id: cleanedQuoteFields?.customer_id }),
            ...(cleanedQuoteFields?.status && { status: cleanedQuoteFields?.status }),
            ...(cleanedQuoteFields?.total_amount && { total_amount: cleanedQuoteFields?.total_amount }),
            ...(cleanedQuoteFields?.subtotal && { subtotal: cleanedQuoteFields?.subtotal }),
            ...(cleanedQuoteFields?.tax_amount && { tax_amount: cleanedQuoteFields?.tax_amount }),
            ...(cleanedQuoteFields?.valid_until && { valid_until: cleanedQuoteFields?.valid_until }),
            updated_at: new Date()?.toISOString()
          };

          console.log('QUOTE UPDATE RETRY - Using minimal fields:', minimalFields);

          const { data: retryQuote, error: retryError } = await supabase
            ?.from('quotes')
            ?.update(minimalFields)
            ?.eq('id', quoteId)
            ?.eq('organization_id', organizationId)
            ?.select('*')
            ?.single();

          if (retryError) {
            console.error('Retry also failed:', retryError);
            return {
              data: null,
              error: {
                message: 'Quote update failed due to persistent schema cache error. The referenced column may not exist in the quotes table.',
                code: 'SCHEMA_CACHE_ERROR',
                details: 'Both primary and retry attempts failed. Please ensure you are using customer_id (not customer) for customer references.',
                originalError: quoteError,
                retryError: retryError
              }
            };
          }

          // If retry succeeded, continue with line items processing
          console.log('QUOTE UPDATE RETRY - Success, proceeding with line items');

          // Update line items if provided (same logic as main path)
          if (line_items && line_items?.length > 0) {
            await this.updateQuoteLineItems(quoteId, line_items, organizationId);
          }

          return {
            data: retryQuote,
            error: {
              type: 'schema_cache_warning',
              message: 'Quote updated successfully using retry mechanism due to schema cache error',
              originalError: quoteError
            }
          };
        }
        throw quoteError;
      }

      // Update line items if provided
      if (line_items && line_items?.length > 0) {
        // Delete existing line items
        const { error: deleteError } = await supabase?.from('quote_line_items')?.delete()?.eq('quote_id', quoteId);

        if (deleteError) throw deleteError;

        // CRITICAL FIX: Get default revenue account for line items without account_id
        let defaultAccountId = await this?.getDefaultRevenueAccount(organizationId);

        if (!defaultAccountId) {
          throw new Error('Cannot update quote line items: No revenue account found for organization. Please ensure at least one revenue account exists in the chart of accounts.');
        }

        // Insert updated line items
        const lineItemsToInsert = line_items?.map((item, index) => ({
          quote_id: quoteId,
          line_number: index + 1,
          item_id: item?.item_id || null,
          item_description: item?.item_description,
          quantity: parseFloat(item?.quantity || 1),
          unit_price: parseFloat(item?.unit_price || 0),
          tax_rate: parseFloat(item?.tax_rate || 0),
          tax_amount: parseFloat(item?.tax_amount || 0),
          notes: item?.notes || null,
          weight_per_unit: parseFloat(item?.weight_per_unit || 1),
          // CRITICAL FIX: Ensure account_id is always provided (never null)
          account_id: item?.account_id || defaultAccountId // Use default revenue account if none provided
        }));

        // CRITICAL FIX: Final validation that all line items have account_id before inserting
        const invalidItems = lineItemsToInsert?.filter(item => !item?.account_id);
        if (invalidItems?.length > 0) {
          throw new Error(`Cannot update quote line items: Missing account_id for ${invalidItems?.length} line item(s). All line items must have a valid account reference.`);
        }

        const { error: lineItemsError } = await supabase?.from('quote_line_items')?.insert(lineItemsToInsert);

        if (lineItemsError) {
          console.error('Line items insertion error:', lineItemsError);
          // CRITICAL FIX: Provide more specific error handling for account_id constraints
          if (lineItemsError?.code === '23502' && lineItemsError?.message?.includes('account_id')) {
            throw new Error('Failed to update quote line items: All line items must have a valid account reference. Please ensure revenue accounts exist in your Chart of Accounts.');
          }
          throw lineItemsError;
        }
      }

      return { data: quote, error: null };
    } catch (error) {
      console.error('Error updating quote:', error);

      // CRITICAL: Handle 22P02 UUID errors with specific messaging for "undefined" string
      if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
        return {
          data: null,
          error: {
            message: 'Invalid UUID format provided for quote ID. The ID must be a valid UUID, not "undefined" or empty string.',
            code: 'UUID_VALIDATION_ERROR',
            details: 'The quote ID must be a valid UUID format. Please ensure the ID is properly formatted and not "undefined". This usually indicates that the quote ID was not properly passed from the frontend component.'
          }
        };
      }

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Quote update failed due to schema cache error. The referenced column may not exist in the quotes table.',
            code: 'SCHEMA_CACHE_ERROR',
            details: 'Please ensure you are using customer_id (not customer) for customer references. If the issue persists, the database schema cache may need to be refreshed.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // Delete quote with constraint checking
  async deleteQuote(quoteId, organizationId) {
    try {
      // Check for related records
      const constraintChecks = await Promise.all([
        // Check delivery challans
        supabase?.from('delivery_challans')?.select('id')?.eq('reference_quote_id', quoteId)?.limit(1),
        // Check payment links
        supabase?.from('payment_links')?.select('id')?.eq('reference_quote_id', quoteId)?.limit(1),
        // Check if converted to invoice
        supabase?.from('quotes')?.select('converted_to_invoice_id')?.eq('id', quoteId)?.not('converted_to_invoice_id', 'is', null)?.limit(1)
      ]);

      const constraints = [];
      const [deliveryChallans, paymentLinks, convertedQuotes] = constraintChecks;

      if (deliveryChallans?.data?.length > 0) constraints?.push('delivery challans');
      if (paymentLinks?.data?.length > 0) constraints?.push('payment links');
      if (convertedQuotes?.data?.length > 0) constraints?.push('converted to invoice');

      if (constraints?.length > 0) {
        return {
          data: null,
          error: {
            type: 'constraint_violation',
            message: `Cannot delete quote. Has related ${constraints?.join(', ')}.`,
            constraints
          }
        };
      }

      const { error } = await supabase?.from('quotes')?.delete()?.eq('id', quoteId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting quote:', error);
      return { data: null, error };
    }
  },

  // Force delete quote
  async forceDeleteQuote(quoteId, organizationId) {
    try {
      const { error } = await supabase?.from('quotes')?.delete()?.eq('id', quoteId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true, cascaded: true }, error: null };
    } catch (error) {
      console.error('Error force deleting quote:', error);
      return { data: null, error };
    }
  }
};

// Payment Operations
export const paymentService = {
  // Get payment links
  async getPaymentLinks(organizationId, filters = {}) {
    try {
      let query = supabase?.from('payment_links')?.select(`
          *,
          customer:customers(id, customer_name, email),
          reference_invoice:invoices!reference_invoice_id(invoice_number),
          reference_quote:quotes!reference_quote_id(quote_number),
          creator:user_profiles!payment_links_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching payment links:', error);
      return { data: [], error };
    }
  },

  // Get payments received
  async getPaymentsReceived(organizationId, filters = {}) {
    try {
      let query = supabase?.from('payments_received')?.select(`
          *,
          customer:customers(id, customer_name, email),
          reference_invoice:invoices!reference_invoice_id(invoice_number),
          reference_payment_link:payment_links!reference_payment_link_id(link_reference),
          creator:user_profiles!payments_received_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.paymentMethod) {
        query = query?.eq('payment_method', filters?.paymentMethod);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching payments received:', error);
      return { data: [], error };
    }
  },

  // Create new payment link with payment_url generation
  async createPaymentLink(linkData, organizationId, userId) {
    try {
      // Generate payment URL based on link reference or use a placeholder
      const generatePaymentUrl = (linkReference) => {
        // In a real application, this would be your domain + payment path
        const baseUrl = process.env.VITE_APP_BASE_URL || window.location?.origin;
        return `${baseUrl}/payment/${linkReference || 'pending'}`;
      };

      // Prepare link data with payment_url
      const processedLinkData = {
        ...linkData,
        // Generate or provide payment_url (required field)
        payment_url: linkData?.payment_url || generatePaymentUrl(linkData?.link_reference),
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('payment_links')?.insert(processedLinkData)?.select()?.single();

      if (error) {
        // Handle payment_url constraint error specifically
        if (error?.code === '23502' && error?.message?.includes('payment_url')) {
          console.error('Payment URL constraint error:', error);

          // Retry with a fallback payment URL
          const fallbackUrl = `${window.location?.origin}/payment/link-${Date.now()}`;
          const retryData = {
            ...processedLinkData,
            payment_url: fallbackUrl
          };

          const { data: retryResult, error: retryError } = await supabase?.from('payment_links')?.insert(retryData)?.select()?.single();

          if (retryError) {
            console.error('Retry failed:', retryError);
            return {
              data: null,
              error: {
                type: 'payment_url_error',
                message: 'Failed to generate payment URL. Please try again.',
                originalError: retryError
              }
            };
          }

          return { data: retryResult, error: null };
        }

        throw error;
      }

      // Update payment_url with actual link_reference if it was auto-generated
      if (data?.link_reference && (!linkData?.payment_url)) {
        const finalPaymentUrl = generatePaymentUrl(data?.link_reference);

        const { data: updatedData, error: updateError } = await supabase
          ?.from('payment_links')
          ?.update({
            payment_url: finalPaymentUrl,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', data?.id)
          ?.eq('organization_id', organizationId)
          ?.select()
          ?.single();

        if (updateError) {
          console.warn('Failed to update payment URL with link reference:', updateError);
          // Still return the original data, as the record was created successfully
          return { data, error: null };
        }

        return { data: updatedData, error: null };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating payment link:', error);
      return { data: null, error };
    }
  },

  // Update payment link
  async updatePaymentLink(paymentLinkId, updates, organizationId) {
    try {
      // CRITICAL FIX: Remove any fields that might cause schema cache errors
      const cleanedUpdates = { ...updates };

      // Remove profile data that causes PGRST204 error - these don't exist as columns
      delete cleanedUpdates?.creator; // FIXED: Remove creator alias (not a column)
      delete cleanedUpdates?.customer; // Remove customer object (not a column)
      delete cleanedUpdates?.reference_invoice; // CRITICAL FIX: Remove reference_invoice alias (not a column)
      delete cleanedUpdates?.reference_quote; // CRITICAL FIX: Remove reference_quote alias (not a column)

      // CRITICAL FIX: Remove generated columns that can only be updated to DEFAULT
      delete cleanedUpdates?.amount_remaining; // FIXED: Auto-generated column (428C9 error)

      // Remove system fields that shouldn't be updated
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      delete cleanedUpdates?.link_reference; // Don't update link reference (auto-generated)

      // Add proper timestamp for update
      cleanedUpdates.updated_at = new Date()?.toISOString();

      console.log('PAYMENT LINK UPDATE - Original data:', updates);
      console.log('PAYMENT LINK UPDATE - Cleaned data:', cleanedUpdates);

      const { data, error } = await supabase
        ?.from('payment_links')
        ?.update(cleanedUpdates)
        ?.eq('id', paymentLinkId)
        ?.eq('organization_id', organizationId)
        ?.select(`
          *,
          customer:customers(id, customer_name, email),
          creator:user_profiles!payment_links_created_by_fkey(full_name)
        `)
        ?.single();

      if (error) {
        // ENHANCED: Handle 428C9 generated column errors specifically
        if (error?.code === '428C9') {
          console.error('Generated column error - column cannot be updated:', error);
          return {
            data: null,
            error: {
              message: 'Generated column error: The "amount_remaining" column is auto-generated and cannot be updated directly.',
              code: 'GENERATED_COLUMN_ERROR',
              details: 'Generated columns can only be updated to DEFAULT. This column is automatically maintained by the database based on other field values.',
              originalError: error
            }
          };
        }

        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error - column not found in payment_links table:', error);
          return {
            data: null,
            error: {
              message: 'Schema error: Referenced column does not exist in the payment_links table. This is a database configuration issue.',
              code: 'SCHEMA_ERROR',
              details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.',
              originalError: error
            }
          };
        }

        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating payment link:', error);

      // CRITICAL: Handle 428C9 generated column errors with specific messaging
      if (error?.code === '428C9') {
        return {
          data: null,
          error: {
            message: 'Generated column error: The "amount_remaining" column is auto-generated and cannot be updated directly.',
            code: 'GENERATED_COLUMN_ERROR',
            details: 'Generated columns can only be updated to DEFAULT. This column is automatically maintained by the database based on other field values.'
          }
        };
      }

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Schema error: Referenced column does not exist in the payment_links table. This is a database configuration issue.',
            code: 'SCHEMA_ERROR',
            details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // Record new payment received with enhanced UUID field validation
  async createPaymentReceived(paymentData, organizationId, userId) {
    try {
      // Process UUID fields to convert empty strings to null values
      // This prevents the "invalid input syntax for type uuid: \"\"" error
      const processedData = {
        ...paymentData,
        // Convert empty string UUID fields to null to prevent database errors
        reference_invoice_id: paymentData?.reference_invoice_id && paymentData?.reference_invoice_id?.trim() !== ''
          ? paymentData?.reference_invoice_id
          : null,
        reference_payment_link_id: paymentData?.reference_payment_link_id && paymentData?.reference_payment_link_id?.trim() !== ''
          ? paymentData?.reference_payment_link_id
          : null,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('payments_received')?.insert(processedData)?.select()?.single();

      if (error) {
        // Enhanced error handling for UUID constraint errors
        if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
          console.error('UUID validation error in payment creation:', error);
          return {
            data: null,
            error: {
              type: 'uuid_validation_error',
              message: 'Invalid UUID format provided. Please ensure all reference fields contain valid UUIDs or leave them blank.',
              originalError: error
            }
          };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating payment received:', error);
      return { data: null, error };
    }
  },

  // Update payment received with enhanced UUID field validation
  async updatePaymentReceived(paymentId, updates, organizationId) {
    try {
      // CRITICAL FIX: Add UUID validation to prevent "undefined" errors
      if (!paymentId || paymentId === 'undefined' || paymentId === null || paymentId === '' || paymentId === undefined) {
        console.error('Invalid payment received ID provided:', paymentId);
        return {
          data: null,
          error: {
            message: 'Invalid payment received ID provided. Cannot update payment without a valid ID.',
            code: 'INVALID_PAYMENT_ID',
            details: 'Payment ID must be a valid UUID, but received: ' + paymentId
          }
        };
      }

      // Validate UUID format for payment ID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(paymentId)) {
        console.error('Invalid UUID format for payment received ID:', paymentId);
        return {
          data: null,
          error: {
            message: 'Invalid payment received ID format. Must be a valid UUID.',
            code: 'INVALID_UUID_FORMAT',
            details: 'Payment ID must be a valid UUID format, but received: ' + paymentId
          }
        };
      }

      // CRITICAL FIX: Validate UUID fields in updates to prevent "undefined" values
      const cleanedUpdates = { ...updates };

      // CRITICAL FIX: Process UUID fields to handle "undefined" values
      if (cleanedUpdates?.customer_id !== undefined) {
        if (cleanedUpdates?.customer_id === 'undefined' || cleanedUpdates?.customer_id === '') {
          cleanedUpdates.customer_id = null; // Convert "undefined" string to null
        } else if (cleanedUpdates?.customer_id && !uuidRegex?.test(cleanedUpdates?.customer_id)) {
          console.error('Invalid customer_id format:', cleanedUpdates?.customer_id);
          return {
            data: null,
            error: {
              message: 'Invalid customer ID format. Must be a valid UUID or null.',
              code: 'INVALID_CUSTOMER_UUID_FORMAT',
              details: 'Customer ID must be a valid UUID format, but received: ' + cleanedUpdates?.customer_id
            }
          };
        }
      }

      if (cleanedUpdates?.reference_invoice_id !== undefined) {
        if (cleanedUpdates?.reference_invoice_id === 'undefined' || cleanedUpdates?.reference_invoice_id === '') {
          cleanedUpdates.reference_invoice_id = null; // Convert "undefined" string to null
        } else if (cleanedUpdates?.reference_invoice_id && !uuidRegex?.test(cleanedUpdates?.reference_invoice_id)) {
          console.error('Invalid reference_invoice_id format:', cleanedUpdates?.reference_invoice_id);
          return {
            data: null,
            error: {
              message: 'Invalid reference invoice ID format. Must be a valid UUID or null.',
              code: 'INVALID_REFERENCE_INVOICE_UUID_FORMAT',
              details: 'Reference invoice ID must be a valid UUID format, but received: ' + cleanedUpdates?.reference_invoice_id
            }
          };
        }
      }

      if (cleanedUpdates?.reference_payment_link_id !== undefined) {
        if (cleanedUpdates?.reference_payment_link_id === 'undefined' || cleanedUpdates?.reference_payment_link_id === '') {
          cleanedUpdates.reference_payment_link_id = null; // Convert "undefined" string to null
        } else if (cleanedUpdates?.reference_payment_link_id && !uuidRegex?.test(cleanedUpdates?.reference_payment_link_id)) {
          console.error('Invalid reference_payment_link_id format:', cleanedUpdates?.reference_payment_link_id);
          return {
            data: null,
            error: {
              message: 'Invalid reference payment link ID format. Must be a valid UUID or null.',
              code: 'INVALID_REFERENCE_PAYMENT_LINK_UUID_FORMAT',
              details: 'Reference payment link ID must be a valid UUID format, but received: ' + cleanedUpdates?.reference_payment_link_id
            }
          };
        }
      }

      if (cleanedUpdates?.cleared_by !== undefined) {
        if (cleanedUpdates?.cleared_by === 'undefined' || cleanedUpdates?.cleared_by === '') {
          cleanedUpdates.cleared_by = null; // Convert "undefined" string to null
        } else if (cleanedUpdates?.cleared_by && !uuidRegex?.test(cleanedUpdates?.cleared_by)) {
          console.error('Invalid cleared_by format:', cleanedUpdates?.cleared_by);
          return {
            data: null,
            error: {
              message: 'Invalid cleared by user ID format. Must be a valid UUID or null.',
              code: 'INVALID_CLEARED_BY_UUID_FORMAT',
              details: 'Cleared by ID must be a valid UUID format, but received: ' + cleanedUpdates?.cleared_by
            }
          };
        }
      }

      // Remove profile data that causes PGRST204 error - these don't exist as columns
      delete cleanedUpdates?.creator; // FIXED: Remove creator alias (not a column)
      delete cleanedUpdates?.cleared_by_profile; // Remove profile data (not part of payments_received table)
      delete cleanedUpdates?.customer; // CRITICAL FIX: Remove customer object (not a column)
      delete cleanedUpdates?.reference_invoice; // CRITICAL FIX: Remove reference_invoice alias (not a column)
      delete cleanedUpdates?.reference_payment_link; // CRITICAL FIX: Remove reference_payment_link alias (not a column)

      // Remove system fields that shouldn't be updated
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      delete cleanedUpdates?.payment_reference; // Don't update payment reference (immutable)

      // Add proper timestamp for update
      cleanedUpdates.updated_at = new Date()?.toISOString();

      console.log('PAYMENT RECEIVED UPDATE - Payment ID:', paymentId);
      console.log('PAYMENT RECEIVED UPDATE - Organization ID:', organizationId);
      console.log('PAYMENT RECEIVED UPDATE - Original data:', updates);
      console.log('PAYMENT RECEIVED UPDATE - Cleaned data:', cleanedUpdates);

      const { data, error } = await supabase
        ?.from('payments_received')
        ?.update(cleanedUpdates)
        ?.eq('id', paymentId)
        ?.eq('organization_id', organizationId)
        ?.select(`
          *,
          customer:customers(id, customer_name, email, company_name),
          creator:user_profiles!payments_received_created_by_fkey(full_name),
          cleared_by_profile:user_profiles!payments_received_cleared_by_fkey(full_name)
        `)
        ?.single();

      if (error) {
        // CRITICAL FIX: Handle 22P02 UUID errors specifically with enhanced error detection
        if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
          console.error('UUID validation error in payment update - parameter details:', {
            paymentId,
            organizationId,
            cleanedUpdates
          });
          return {
            data: null,
            error: {
              message: 'Invalid UUID format provided. One or more UUID fields contain invalid values like "undefined".',
              code: 'UUID_VALIDATION_ERROR',
              details: 'All UUID fields must be valid UUID format or null. Please ensure payment ID, customer ID, and reference IDs are properly formatted and not "undefined".',
              originalError: error
            }
          };
        }

        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error - column not found in payments_received table:', error);
          return {
            data: null,
            error: {
              message: 'Schema error: Referenced column does not exist in the payments_received table. This is a database configuration issue.',
              code: 'SCHEMA_ERROR',
              details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.',
              originalError: error
            }
          };
        }

        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating payment received:', error);

      // CRITICAL: Handle 22P02 UUID errors with specific messaging for "undefined" string
      if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
        return {
          data: null,
          error: {
            message: 'Invalid UUID format provided. One or more UUID fields contain invalid values like "undefined".',
            code: 'UUID_VALIDATION_ERROR',
            details: 'All UUID fields must be valid UUID format or null. Please ensure payment ID, customer ID, and reference IDs are properly formatted and not "undefined". This usually indicates that the ID was not properly passed from the frontend component.'
          }
        };
      }

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Schema error: Referenced column does not exist in the payments_received table. This is a database configuration issue.',
            code: 'SCHEMA_ERROR',
            details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // Delete payment link
  async deletePaymentLink(paymentLinkId, organizationId) {
    try {
      // Check for related payments received
      const { data: relatedPayments } = await supabase?.from('payments_received')?.select('id')?.eq('reference_payment_link_id', paymentLinkId)?.limit(1);

      if (relatedPayments?.length > 0) {
        return {
          data: null,
          error: {
            type: 'constraint_violation',
            message: 'Cannot delete payment link. Has related payments received.',
            constraints: ['payments received']
          }
        };
      }

      const { error } = await supabase?.from('payment_links')?.delete()?.eq('id', paymentLinkId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting payment link:', error);
      return { data: null, error };
    }
  },

  // Delete payment received
  async deletePaymentReceived(paymentId, organizationId) {
    try {
      const { error } = await supabase?.from('payment_links')?.delete()?.eq('id', paymentId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting payment received:', error);
      return { data: null, error };
    }
  },

  // Get payment link details
  async getPaymentLinkDetails(paymentLinkId, organizationId) {
    try {
      const { data, error } = await supabase?.from('payment_links')?.select(`
          *,
          customer:customers(id, customer_name, email, company_name),
          reference_invoice:invoices!reference_invoice_id(id, invoice_number, total_amount),
          reference_quote:quotes!reference_quote_id(id, quote_number, total_amount),
          payments_received(id, amount_received, payment_date, status),
          creator:user_profiles!payment_links_created_by_fkey(full_name, email)
        `)?.eq('id', paymentLinkId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching payment link details:', error);
      return { data: null, error };
    }
  },

  // Get payment received details
  async getPaymentReceivedDetails(paymentId, organizationId) {
    try {
      const { data, error } = await supabase?.from('payments_received')?.select(`
          *,
          customer:customers(id, customer_name, email, company_name),
          reference_invoice:invoices!reference_invoice_id(id, invoice_number, total_amount),
          reference_payment_link:payment_links!reference_payment_link_id(id, link_reference, amount),
          creator:user_profiles!payments_received_created_by_fkey(full_name, email),
          cleared_by_profile:user_profiles!payments_received_cleared_by_fkey(full_name, email)
        `)?.eq('id', paymentId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching payment received details:', error);
      return { data: null, error };
    }
  }
};

// Delivery Operations
export const deliveryService = {
  // Get delivery challans
  async getDeliveryChallans(organizationId, filters = {}) {
    try {
      let query = supabase?.from('delivery_challans')?.select(`
          *,
          customer:customers(id, customer_name, email),
          reference_invoice:invoices(invoice_number),
          reference_quote:quotes(quote_number),
          creator:user_profiles!delivery_challans_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching delivery challans:', error);
      return { data: [], error };
    }
  },

  // Create new delivery challan
  async createDeliveryChallan(challanData, organizationId, userId) {
    try {
      const { data, error } = await supabase?.from('delivery_challans')?.insert({
        ...challanData,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating delivery challan:', error);
      return { data: null, error };
    }
  },

  // Update delivery challan
  async updateDeliveryChallan(challanId, updates, organizationId) {
    try {
      // CRITICAL FIX: Remove any fields that might cause schema cache errors
      const cleanedUpdates = { ...updates };

      // Remove profile data that causes PGRST204 error - these don't exist as columns
      delete cleanedUpdates?.creator; // FIXED: Remove creator alias (not a column)
      delete cleanedUpdates?.customer; // Remove customer object (not a column)
      delete cleanedUpdates?.reference_invoice; // Remove invoice object (not a column)
      delete cleanedUpdates?.reference_quote; // Remove quote object (not a column)

      // Remove system fields that shouldn't be updated
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      delete cleanedUpdates?.challan_number; // Don't update challan number (auto-generated)

      // Add proper timestamp for update
      cleanedUpdates.updated_at = new Date()?.toISOString();

      console.log('DELIVERY CHALLAN UPDATE - Original data:', updates);
      console.log('DELIVERY CHALLAN UPDATE - Cleaned data:', cleanedUpdates);

      const { data, error } = await supabase
        ?.from('delivery_challans')
        ?.update(cleanedUpdates)
        ?.eq('id', challanId)
        ?.eq('organization_id', organizationId)
        ?.select(`
          *,
          customer:customers(id, customer_name, email),
          reference_invoice:invoices(invoice_number),
          reference_quote:quotes(quote_number),
          creator:user_profiles!delivery_challans_created_by_fkey(full_name)
        `)
        ?.single();

      if (error) {
        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error - column not found in delivery_challans table:', error);
          return {
            data: null,
            error: {
              message: 'Schema error: Referenced column does not exist in the delivery_challans table. This is a database configuration issue.',
              code: 'SCHEMA_ERROR',
              details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.',
              originalError: error
            }
          };
        }

        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating delivery challan:', error);

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Schema error: Referenced column does not exist in the delivery_challans table. This is a database configuration issue.',
            code: 'SCHEMA_ERROR',
            details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // Delete delivery challan
  async deleteDeliveryChallan(challanId, organizationId) {
    try {
      // No major constraints expected for delivery challans
      const { error } = await supabase?.from('delivery_challans')?.delete()?.eq('id', challanId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting delivery challan:', error);
      return { data: null, error };
    }
  },

  // Get delivery challan details
  async getDeliveryChallanDetails(challanId, organizationId) {
    try {
      const { data, error } = await supabase?.from('delivery_challans')?.select(`
          *,
          customer:customers(id, customer_name, email, company_name),
          reference_invoice:invoices(id, invoice_number),
          reference_quote:quotes(id, quote_number),
          delivery_challan_items(*),
          creator:user_profiles!delivery_challans_created_by_fkey(full_name, email)
        `)?.eq('id', challanId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching delivery challan details:', error);
      return { data: null, error };
    }
  }
};

// Retainer Operations
export const retainerService = {
  // Get retainer invoices
  async getRetainerInvoices(organizationId, filters = {}) {
    try {
      let query = supabase?.from('retainer_invoices')?.select(`
          *,
          customer:customers(id, customer_name, email),
          creator:user_profiles!retainer_invoices_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching retainer invoices:', error);
      return { data: [], error };
    }
  },

  // Create new retainer invoice with enhanced date validation and error handling
  async createRetainerInvoice(retainerData, organizationId, userId) {
    try {
      // Validate and process date fields
      const processedData = {
        ...retainerData,
        // Handle start_date (required field)
        start_date: retainerData?.start_date ?
          (retainerData?.start_date === '' ? new Date()?.toISOString()?.split('T')?.[0] : retainerData?.start_date)
          : new Date()?.toISOString()?.split('T')?.[0],
        // Handle end_date (optional field)
        end_date: retainerData?.end_date && retainerData?.end_date !== '' ? retainerData?.end_date : null,
        // Ensure issue_date is properly formatted
        issue_date: retainerData?.issue_date && retainerData?.issue_date !== '' ? retainerData?.issue_date : new Date()?.toISOString()?.split('T')?.[0]
      };

      // Generate the retainer number using the database function
      const { data: retainerNumberData, error: numberError } = await supabase
        ?.rpc('generate_retainer_number', { org_id: organizationId });

      if (numberError) {
        console.error('Error generating retainer number:', numberError);
        // If function fails, generate a fallback retainer number
        const fallbackNumber = `RET-${new Date()?.getFullYear()}-${String(Date.now())?.slice(-4)}`;
        console.warn('Using fallback retainer number:', fallbackNumber);

        const { data, error } = await supabase?.from('retainer_invoices')?.insert({
          ...processedData,
          retainer_number: fallbackNumber,
          organization_id: organizationId,
          created_by: userId,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.select()?.single();

        if (error) throw error;
        return { data, error: null };
      }

      const retainerNumber = retainerNumberData;

      // Create the retainer invoice with the generated retainer number and validated dates
      const { data, error } = await supabase?.from('retainer_invoices')?.insert({
        ...processedData,
        retainer_number: retainerNumber,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.select()?.single();

      if (error) {
        // If primary insert fails due to retainer_number constraint, try with fallback
        if (error?.code === '23502' && error?.message?.includes('retainer_number')) {
          console.warn('Retainer number constraint error, retrying with fallback');
          const fallbackNumber = `RET-${new Date()?.getFullYear()}-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 5)}`;

          const { data: retryData, error: retryError } = await supabase?.from('retainer_invoices')?.insert({
            ...processedData,
            retainer_number: fallbackNumber,
            organization_id: organizationId,
            created_by: userId,
            created_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString()
          })?.select()?.single();

          if (retryError) throw retryError;
          return { data: retryData, error: null };
        }

        // Handle date constraint errors specifically
        if (error?.code === '22007' && error?.message?.includes('date')) {
          console.error('Date validation error in retainer invoice creation:', error);
          return {
            data: null,
            error: {
              type: 'date_validation_error',
              message: 'Invalid date format provided. Please ensure all dates are in valid YYYY-MM-DD format.',
              originalError: error
            }
          };
        }

        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating retainer invoice:', error);
      return { data: null, error };
    }
  },

  // Update retainer invoice with date validation
  async updateRetainerInvoice(retainerId, updates, organizationId) {
    try {
      // CRITICAL FIX: Enhanced UUID validation to prevent "undefined" errors
      if (!retainerId || retainerId === 'undefined' || retainerId === null || retainerId === '' || retainerId === undefined) {
        console.error('Invalid retainer invoice ID provided:', retainerId);
        return {
          data: null,
          error: {
            message: 'Invalid retainer invoice ID provided. Cannot update retainer invoice without a valid ID.',
            code: 'INVALID_RECURRING_INVOICE_ID',
            details: 'Retainer invoice ID must be a valid UUID, but received: ' + retainerId
          }
        };
      }

      // Validate UUID format for retainer invoice ID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(retainerId)) {
        console.error('Invalid UUID format for retainer invoice ID:', retainerId);
        return {
          data: null,
          error: {
            message: 'Invalid retainer invoice ID format. Must be a valid UUID.',
            code: 'INVALID_UUID_FORMAT',
            details: 'Retainer invoice ID must be a valid UUID format, but received: ' + retainerId
          }
        };
      }

      // CRITICAL FIX: Remove any fields that might cause schema cache errors
      const cleanedUpdates = { ...updates };

      // Remove profile data that causes PGRST204 error - these don't exist as columns
      delete cleanedUpdates?.creator; // FIXED: Remove creator alias (not a column)
      delete cleanedUpdates?.customer; // Remove customer object (not a column)

      // CRITICAL FIX: Remove generated columns that can only be updated to DEFAULT
      delete cleanedUpdates?.remaining_amount; // FIXED: Auto-generated column (428C9 error)
      delete cleanedUpdates?.utilization_percentage; // FIXED: Auto-generated column (428C9 error)

      // Remove system fields that shouldn't be updated
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID
      delete cleanedUpdates?.retainer_number; // Don't update retainer number (immutable)

      // Process date fields in updates
      const processedUpdates = {
        ...cleanedUpdates,
        // Handle date fields if present in updates
        ...(cleanedUpdates?.start_date !== undefined && {
          start_date: cleanedUpdates?.start_date && cleanedUpdates?.start_date !== '' ? cleanedUpdates?.start_date : new Date()?.toISOString()?.split('T')?.[0]
        }),
        ...(cleanedUpdates?.end_date !== undefined && {
          end_date: cleanedUpdates?.end_date && cleanedUpdates?.end_date !== '' ? cleanedUpdates?.end_date : null
        }),
        ...(cleanedUpdates?.issue_date !== undefined && {
          issue_date: cleanedUpdates?.issue_date && cleanedUpdates?.issue_date !== '' ? cleanedUpdates?.issue_date : new Date()?.toISOString()?.split('T')?.[0]
        }),
        updated_at: new Date()?.toISOString()
      };

      console.log('RETAINER UPDATE - Original data:', updates);
      console.log('RETAINER UPDATE - Cleaned data:', processedUpdates);

      const { data, error } = await supabase
        ?.from('retainer_invoices')
        ?.update(processedUpdates)
        ?.eq('id', retainerId)
        ?.eq('organization_id', organizationId)
        ?.select(`
          *,
          customer:customers(id, customer_name, email),
          creator:user_profiles!retainer_invoices_created_by_fkey(full_name)
        `)
        ?.single();

      if (error) {
        // CRITICAL FIX: Handle 22P02 UUID errors specifically with enhanced error detection
        if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
          console.error('UUID validation error - invalid UUID format:', error);
          return {
            data: null,
            error: {
              message: 'Invalid UUID format provided. The ID must be a valid UUID, not "undefined" or empty string.',
              code: 'UUID_VALIDATION_ERROR',
              details: 'UUID fields must be in valid UUID format. Please ensure customer_id and other UUID fields are properly formatted and not "undefined". This usually indicates that the ID was not properly passed from the frontend component.',
              originalError: error
            }
          };
        }

        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error - column not found in retainer_invoices table:', error);
          return {
            data: null,
            error: {
              message: 'Schema error: Referenced column does not exist in the retainer_invoices table. This is a database configuration issue.',
              code: 'SCHEMA_ERROR',
              details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.',
              originalError: error
            }
          };
        }

        // Handle date constraint errors specifically
        if (error?.code === '22007' && error?.message?.includes('date')) {
          console.error('Date validation error in retainer invoice update:', error);
          return {
            data: null,
            error: {
              type: 'date_validation_error',
              message: 'Invalid date format provided. Please ensure all dates are in valid YYYY-MM-DD format.',
              originalError: error
            }
          };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating retainer invoice:', error);

      // CRITICAL: Handle 22P02 UUID errors with specific messaging for "undefined" string
      if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
        return {
          data: null,
          error: {
            message: 'Invalid UUID format provided. The ID must be a valid UUID, not "undefined" or empty string.',
            code: 'UUID_VALIDATION_ERROR',
            details: 'UUID fields must be in valid UUID format. Please ensure customer_id and other UUID fields are properly formatted and not "undefined". This usually indicates that the ID was not properly passed from the frontend component.'
          }
        };
      }

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Schema error: Referenced column does not exist in the retainer_invoices table. This is a database configuration issue.',
            code: 'SCHEMA_ERROR',
            details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // Delete retainer invoice
  async deleteRetainerInvoice(retainerId, organizationId) {
    try {
      // Check for utilizations
      const { data: utilizations } = await supabase?.from('retainer_utilizations')?.select('id')?.eq('retainer_invoice_id', retainerId)?.limit(1);

      if (utilizations?.length > 0) {
        return {
          data: null,
          error: {
            type: 'constraint_violation',
            message: 'Cannot delete retainer invoice. Has related utilizations.',
            constraints: ['retainer utilizations']
          }
        };
      }

      const { error } = await supabase?.from('retainer_invoices')?.delete()?.eq('id', retainerId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting retainer invoice:', error);
      return { data: null, error };
    }
  },

  // Force delete retainer invoice
  async forceDeleteRetainerInvoice(retainerId, organizationId) {
    try {
      const { error } = await supabase?.from('retainer_invoices')?.delete()?.eq('id', retainerId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true, cascaded: true }, error: null };
    } catch (error) {
      console.error('Error force deleting retainer invoice:', error);
      return { data: null, error };
    }
  },

  // Get retainer invoice details
  async getRetainerInvoiceDetails(retainerId, organizationId) {
    try {
      const { data, error } = await supabase?.from('retainer_invoices')?.select(`
          *,
          customer:customers(id, customer_name, email, company_name),
          retainer_utilizations(id, amount_utilized, created_at, reference_invoice:invoices(invoice_number)),
          creator:user_profiles!retainer_invoices_created_by_fkey(full_name, email)
        `)?.eq('id', retainerId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching retainer invoice details:', error);
      return { data: null, error };
    }
  }
};

// Recurring Invoice Operations
export const recurringService = {
  // Get recurring invoices
  async getRecurringInvoices(organizationId, filters = {}) {
    try {
      let query = supabase?.from('recurring_invoices')?.select(`
          *,
          customer:customers(id, customer_name, email),
          creator:user_profiles!recurring_invoices_created_by_fkey(full_name)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching recurring invoices:', error);
      return { data: [], error };
    }
  },

  // Create new recurring invoice with enhanced date validation and error handling
  async createRecurringInvoice(recurringData, organizationId, userId) {
    try {
      // Validate and process date fields to prevent empty string errors
      const processedData = {
        ...recurringData,
        // Handle start_date (required field - NOT nullable)
        start_date: recurringData?.start_date ?
          (recurringData?.start_date === '' ? new Date()?.toISOString()?.split('T')?.[0] : recurringData?.start_date)
          : new Date()?.toISOString()?.split('T')?.[0],
        // Handle end_date (optional field - nullable)
        end_date: recurringData?.end_date && recurringData?.end_date !== '' ? recurringData?.end_date : null,
        // Handle next_billing_date (required field - NOT nullable)
        next_billing_date: recurringData?.next_billing_date ?
          (recurringData?.next_billing_date === '' ? new Date()?.toISOString()?.split('T')?.[0] : recurringData?.next_billing_date)
          : new Date()?.toISOString()?.split('T')?.[0],
        // Handle total_cycles (integer field - prevent empty string)
        total_cycles: (() => {
          if (recurringData?.total_cycles === '' || recurringData?.total_cycles === null || recurringData?.total_cycles === undefined) {
            return null; // Allow null for unlimited cycles
          }
          const cycles = parseInt(recurringData?.total_cycles, 10);
          return isNaN(cycles) ? null : cycles;
        })()
      };

      const { data, error } = await supabase?.from('recurring_invoices')?.insert({
        ...processedData,
        organization_id: organizationId,
        created_by: userId,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      })?.select()?.single();

      if (error) {
        // Handle integer conversion errors specifically
        if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type integer')) {
          console.error('Integer validation error in recurring invoice creation:', error);
          return {
            data: null,
            error: {
              type: 'integer_validation_error',
              message: 'Invalid number format provided. Please ensure numeric fields contain valid numbers or leave them blank.',
              originalError: error
            }
          };
        }

        // Handle date constraint errors specifically
        if (error?.code === '22007' && error?.message?.includes('date')) {
          console.error('Date validation error in recurring invoice creation:', error);
          return {
            data: null,
            error: {
              type: 'date_validation_error',
              message: 'Invalid date format provided. Please ensure all dates are in valid YYYY-MM-DD format.',
              originalError: error
            }
          };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating recurring invoice:', error);
      return { data: null, error };
    }
  },

  // Update recurring invoice with date validation
  async updateRecurringInvoice(recurringId, updates, organizationId) {
    try {
      // CRITICAL FIX: Enhanced UUID validation to prevent "undefined" errors
      if (!recurringId || recurringId === 'undefined' || recurringId === null || recurringId === '' || recurringId === undefined) {
        console.error('Invalid recurring invoice ID provided:', recurringId);
        return {
          data: null,
          error: {
            message: 'Invalid recurring invoice ID provided. Cannot update recurring invoice without a valid ID.',
            code: 'INVALID_RECURRING_INVOICE_ID',
            details: 'Recurring invoice ID must be a valid UUID, but received: ' + recurringId
          }
        };
      }

      // Validate UUID format for recurring invoice ID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(recurringId)) {
        console.error('Invalid UUID format for recurring invoice ID:', recurringId);
        return {
          data: null,
          error: {
            message: 'Invalid recurring invoice ID format. Must be a valid UUID.',
            code: 'INVALID_UUID_FORMAT',
            details: 'Recurring invoice ID must be a valid UUID format, but received: ' + recurringId
          }
        };
      }

      // CRITICAL FIX: Remove any fields that might cause schema cache errors
      const cleanedUpdates = { ...updates };

      // Remove profile data that causes PGRST204 error - these don't exist as columns
      delete cleanedUpdates?.creator; // FIXED: Remove creator alias (not a column)
      delete cleanedUpdates?.customer; // Remove customer object (not a column)

      // Remove system fields that shouldn't be updated
      delete cleanedUpdates?.id; // Don't update primary key
      delete cleanedUpdates?.created_at; // Don't update creation timestamp
      delete cleanedUpdates?.organization_id; // Don't update organization ID
      delete cleanedUpdates?.created_by; // Don't update creator ID

      // CRITICAL FIX: Enhanced customer_id field processing with UUID validation
      if (cleanedUpdates?.customer_id !== undefined) {
        // Validate customer_id if it's provided
        if (cleanedUpdates?.customer_id === 'undefined' || cleanedUpdates?.customer_id === '' || cleanedUpdates?.customer_id === null) {
          console.error('Invalid customer_id provided:', cleanedUpdates?.customer_id);
          return {
            data: null,
            error: {
              message: 'Invalid customer ID provided. Customer ID must be a valid UUID or null.',
              code: 'INVALID_CUSTOMER_ID',
              details: 'Customer ID must be a valid UUID format, but received: ' + cleanedUpdates?.customer_id
            }
          };
        }

        // Validate UUID format for customer_id if it's not null
        if (cleanedUpdates?.customer_id && !uuidRegex?.test(cleanedUpdates?.customer_id)) {
          console.error('Invalid UUID format for customer_id:', cleanedUpdates?.customer_id);
          return {
            data: null,
            error: {
              message: 'Invalid customer ID format. Must be a valid UUID.',
              code: 'INVALID_CUSTOMER_UUID_FORMAT',
              details: 'Customer ID must be a valid UUID format, but received: ' + cleanedUpdates?.customer_id
            }
          };
        }
      }

      // Process date fields in updates to prevent empty string errors
      const processedUpdates = {
        ...cleanedUpdates,
        // Handle date fields if present in updates
        ...(cleanedUpdates?.start_date !== undefined && {
          start_date: cleanedUpdates?.start_date && cleanedUpdates?.start_date !== '' ? cleanedUpdates?.start_date : new Date()?.toISOString()?.split('T')?.[0]
        }),
        ...(cleanedUpdates?.end_date !== undefined && {
          end_date: cleanedUpdates?.end_date && cleanedUpdates?.end_date !== '' ? cleanedUpdates?.end_date : null
        }),
        ...(cleanedUpdates?.next_billing_date !== undefined && {
          next_billing_date: cleanedUpdates?.next_billing_date && cleanedUpdates?.next_billing_date !== '' ? cleanedUpdates?.next_billing_date : new Date()?.toISOString()?.split('T')?.[0]
        }),
        // Handle total_cycles updates (integer field - prevent empty string)
        ...(cleanedUpdates?.total_cycles !== undefined && {
          total_cycles: (() => {
            if (cleanedUpdates?.total_cycles === '' || cleanedUpdates?.total_cycles === null || cleanedUpdates?.total_cycles === undefined) {
              return null; // Allow null for unlimited cycles
            }
            const cycles = parseInt(cleanedUpdates?.total_cycles, 10);
            return isNaN(cycles) ? null : cycles;
          })()
        }),
        updated_at: new Date()?.toISOString()
      };

      console.log('RECURRING INVOICE UPDATE - Recurring ID:', recurringId);
      console.log('RECURRING INVOICE UPDATE - Organization ID:', organizationId);
      console.log('RECURRING INVOICE UPDATE - Original data:', updates);
      console.log('RECURRING INVOICE UPDATE - Processed data:', processedUpdates);

      const { data, error } = await supabase
        ?.from('recurring_invoices')
        ?.update(processedUpdates)
        ?.eq('id', recurringId)
        ?.eq('organization_id', organizationId)
        ?.select(`
          *,
          customer:customers(id, customer_name, email),
          creator:user_profiles!recurring_invoices_created_by_fkey(full_name)
        `)
        ?.single();

      if (error) {
        // CRITICAL FIX: Handle 22P02 UUID errors specifically with enhanced error detection
        if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
          console.error('UUID validation error - invalid UUID format:', error);
          return {
            data: null,
            error: {
              message: 'Invalid UUID format provided. The ID must be a valid UUID, not "undefined" or empty string.',
              code: 'UUID_VALIDATION_ERROR',
              details: 'UUID fields must be in valid UUID format. Please ensure customer_id and other UUID fields are properly formatted and not "undefined". This usually indicates that the ID was not properly passed from the frontend component.',
              originalError: error
            }
          };
        }

        // ENHANCED: Handle PGRST204 schema cache errors specifically
        if (error?.code === 'PGRST204') {
          console.error('Schema cache error - column not found in recurring_invoices table:', error);
          return {
            data: null,
            error: {
              message: 'Schema error: Referenced column does not exist in the recurring_invoices table. This is a database configuration issue.',
              code: 'SCHEMA_ERROR',
              details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.',
              originalError: error
            }
          };
        }

        // Handle integer conversion errors specifically
        if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type integer')) {
          console.error('Integer validation error in recurring invoice update:', error);
          return {
            data: null,
            error: {
              type: 'integer_validation_error',
              message: 'Invalid number format provided. Please ensure numeric fields contain valid numbers or leave them blank.',
              originalError: error
            }
          };
        }

        // Handle date constraint errors specifically
        if (error?.code === '22007' && error?.message?.includes('date')) {
          console.error('Date validation error in recurring invoice update:', error);
          return {
            data: null,
            error: {
              type: 'date_validation_error',
              message: 'Invalid date format provided. Please ensure all dates are in valid YYYY-MM-DD format.',
              originalError: error
            }
          };
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating recurring invoice:', error);

      // CRITICAL: Handle 22P02 UUID errors with specific messaging for "undefined" string
      if (error?.code === '22P02' && error?.message?.includes('invalid input syntax for type uuid')) {
        return {
          data: null,
          error: {
            message: 'Invalid UUID format provided. The ID must be a valid UUID, not "undefined" or empty string.',
            code: 'UUID_VALIDATION_ERROR',
            details: 'UUID fields must be in valid UUID format. Please ensure customer_id and other UUID fields are properly formatted and not "undefined". This usually indicates that the ID was not properly passed from the frontend component.'
          }
        };
      }

      // CRITICAL: Handle PGRST204 errors with specific messaging
      if (error?.code === 'PGRST204') {
        return {
          data: null,
          error: {
            message: 'Schema error: Referenced column does not exist in the recurring_invoices table. This is a database configuration issue.',
            code: 'SCHEMA_ERROR',
            details: 'The referenced field is a join alias or non-existent column, and cannot be updated directly.'
          }
        };
      }

      return { data: null, error };
    }
  },

  // Delete recurring invoice
  async deleteRecurringInvoice(recurringId, organizationId) {
    try {
      // Check for generated invoices
      const { data: history } = await supabase?.from('recurring_invoice_history')?.select('id')?.eq('recurring_invoice_id', recurringId)?.limit(1);

      if (history?.length > 0) {
        return {
          data: null,
          error: {
            type: 'constraint_violation',
            message: 'Cannot delete recurring invoice. Has generated invoice history.',
            constraints: ['invoice history']
          }
        };
      }

      const { error } = await supabase?.from('recurring_invoices')?.delete()?.eq('id', recurringId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting recurring invoice:', error);
      return { data: null, error };
    }
  },

  // Force delete recurring invoice
  async forceDeleteRecurringInvoice(recurringId, organizationId) {
    try {
      const { error } = await supabase?.from('recurring_invoices')?.delete()?.eq('id', recurringId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true, cascaded: true }, error: null };
    } catch (error) {
      console.error('Error force deleting recurring invoice:', error);
      return { data: null, error };
    }
  },

  // Get recurring invoice details
  async getRecurringInvoiceDetails(recurringId, organizationId) {
    try {
      const { data, error } = await supabase?.from('recurring_invoices')?.select(`
          *,
          customer:customers(id, customer_name, email, company_name),
          recurring_invoice_history(id, generated_at, generated_invoice:invoices(id, invoice_number, status)),
          creator:user_profiles!recurring_invoices_created_by_fkey(full_name, email)
        `)?.eq('id', recurringId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching recurring invoice details:', error);
      return { data: null, error };
    }
  }
};

// Enhanced Credit Note Service with Line Items Support
export const creditNoteService = {
  // Get credit notes
  async getCreditNotes(organizationId, filters = {}) {
    try {
      let query = supabase?.from('credit_notes')?.select(`
          *,
          customer:customers(*),
          reference_invoice:invoices(*),
          credit_note_line_items(
            *,
            item:items(*),
            account:chart_of_accounts(*)
          )
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.customer_id) {
        query = query?.eq('customer_id', filters?.customer_id);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching credit notes:', error);
      return { data: [], error };
    }
  },

  // Get credit note details
  async getCreditNoteDetails(creditNoteId, organizationId) {
    try {
      const { data, error } = await supabase?.from('credit_notes')?.select(`
          *,
          customer:customers(*),
          reference_invoice:invoices(*),
          credit_note_line_items(
            *,
            item:items(*),
            account:chart_of_accounts(*)
          )
        `)?.eq('id', creditNoteId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching credit note details:', error);
      return { data: null, error };
    }
  },

  async createCreditNote(creditNoteData, organizationId, userId) {
    try {
      const { line_items, ...creditNoteFields } = creditNoteData;

      // Start transaction
      const { data: creditNote, error: creditNoteError } = await supabase?.from('credit_notes')?.insert({
        ...creditNoteFields,
        organization_id: organizationId,
        created_by: userId
      })?.select()?.single();

      if (creditNoteError) throw creditNoteError;

      // Create line items if provided
      if (line_items && line_items?.length > 0) {
        const lineItemsToInsert = line_items?.map((item, index) => ({
          credit_note_id: creditNote?.id,
          line_number: index + 1,
          item_id: item?.item_id || null,
          item_description: item?.item_description,
          quantity: parseFloat(item?.quantity || 1),
          unit_price: parseFloat(item?.unit_price || 0),
          tax_rate: parseFloat(item?.tax_rate || 0),
          tax_amount: parseFloat(item?.tax_amount || 0),
          notes: item?.notes || null,
          account_id: item?.account_id || null
        }));

        const { error: lineItemsError } = await supabase?.from('credit_note_line_items')?.insert(lineItemsToInsert);

        if (lineItemsError) throw lineItemsError;
      }

      return { data: creditNote, error: null };
    } catch (error) {
      console.error('Error creating credit note:', error);
      return { data: null, error };
    }
  },

  async updateCreditNote(creditNoteId, creditNoteData, organizationId) {
    try {
      const { line_items, ...creditNoteFields } = creditNoteData;

      // Update credit note
      const { data: creditNote, error: creditNoteError } = await supabase?.from('credit_notes')?.update(creditNoteFields)?.eq('id', creditNoteId)?.eq('organization_id', organizationId)?.select()?.single();

      if (creditNoteError) throw creditNoteError;

      // Update line items if provided
      if (line_items && line_items?.length > 0) {
        // Delete existing line items
        const { error: deleteError } = await supabase?.from('credit_note_line_items')?.delete()?.eq('credit_note_id', creditNoteId);

        if (deleteError) throw deleteError;

        // Insert updated line items
        const lineItemsToInsert = line_items?.map((item, index) => ({
          credit_note_id: creditNoteId,
          line_number: index + 1,
          item_id: item?.item_id || null,
          item_description: item?.item_description,
          quantity: parseFloat(item?.quantity || 1),
          unit_price: parseFloat(item?.unit_price || 0),
          tax_rate: parseFloat(item?.tax_rate || 0),
          tax_amount: parseFloat(item?.tax_amount || 0),
          notes: item?.notes || null,
          account_id: item?.account_id || null
        }));

        const { error: lineItemsError } = await supabase?.from('credit_note_line_items')?.insert(lineItemsToInsert);

        if (lineItemsError) throw lineItemsError;
      }

      return { data: creditNote, error: null };
    } catch (error) {
      console.error('Error updating credit note:', error);
      return { data: null, error };
    }
  },

  // Delete credit note
  async deleteCreditNote(creditNoteId, organizationId) {
    try {
      // Credit notes typically don't have major constraints preventing deletion
      const { error } = await supabase?.from('credit_notes')?.delete()?.eq('id', creditNoteId)?.eq('organization_id', organizationId);

      if (error) throw error;
      return { data: { deleted: true }, error: null };
    } catch (error) {
      console.error('Error deleting credit note:', error);
      return { data: null, error };
    }
  }
};

// Sales Metrics Operations
export const salesMetricsService = {
  // Get comprehensive sales metrics
  async getSalesMetrics(organizationId) {
    try {
      // Get total outstanding amounts
      const { data: outstandingData } = await supabase?.from('customers')?.select('total_outstanding')?.eq('organization_id', organizationId)?.eq('status', 'active');

      const totalOutstanding = outstandingData?.reduce((sum, customer) =>
        sum + (parseFloat(customer?.total_outstanding) || 0), 0) || 0;

      // Get overdue invoices
      const { data: overdueData } = await supabase?.from('invoices')?.select('total_amount, amount_paid')?.eq('organization_id', organizationId)?.eq('status', 'overdue');

      const overdueAmount = overdueData?.reduce((sum, invoice) =>
        sum + (parseFloat(invoice?.total_amount) || 0) - (parseFloat(invoice?.amount_paid) || 0), 0) || 0;

      // Get quote conversion rate
      const { data: quotesData } = await supabase?.from('quotes')?.select('status')?.eq('organization_id', organizationId);

      const totalQuotes = quotesData?.length || 0;
      const convertedQuotes = quotesData?.filter(q => q?.status === 'accepted' || q?.status === 'converted')?.length || 0;
      const conversionRate = totalQuotes > 0 ? (convertedQuotes / totalQuotes * 100) : 0;

      // Get average payment time
      const { data: paymentsData } = await supabase?.from('invoices')?.select('issue_date, paid_at')?.eq('organization_id', organizationId)?.eq('status', 'paid')?.not('paid_at', 'is', null)?.limit(100);

      let avgPaymentTime = null; // No default value
      if (paymentsData && paymentsData?.length > 0) {
        const paymentTimes = paymentsData?.map(invoice => {
          const issueDate = new Date(invoice?.issue_date);
          const paidDate = new Date(invoice?.paid_at);
          return Math.floor((paidDate - issueDate) / (1000 * 60 * 60 * 24));
        })?.filter(time => time > 0);

        if (paymentTimes?.length > 0) {
          avgPaymentTime = Math.round(paymentTimes?.reduce((sum, time) => sum + time, 0) / paymentTimes?.length);
        }
      }

      return {
        data: {
          totalOutstanding,
          overdueAmount,
          conversionRate,
          avgPaymentTime
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching sales metrics:', error);
      return { data: null, error };
    }
  }
};

// Real-time subscriptions for live updates
export const salesSubscriptions = {
  // Subscribe to customer changes
  subscribeToCustomers(organizationId, callback) {
    return supabase?.channel(`customers-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'customers',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        // Enhanced payload with table info for better handling
        callback({
          ...payload,
          table: 'customers',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to invoice changes
  subscribeToInvoices(organizationId, callback) {
    return supabase?.channel(`invoices-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'invoices',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'invoices',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to payment changes
  subscribeToPayments(organizationId, callback) {
    return supabase?.channel(`payments-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'payments_received',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'payments_received',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to quotes changes
  subscribeToQuotes(organizationId, callback) {
    return supabase?.channel(`quotes-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'quotes',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'quotes',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to payment links changes
  subscribeToPaymentLinks(organizationId, callback) {
    return supabase?.channel(`payment-links-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'payment_links',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'payment_links',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to delivery challans changes
  subscribeToDeliveryChallans(organizationId, callback) {
    return supabase?.channel(`delivery-challans-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'delivery_challans',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'delivery_challans',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to retainer invoices changes
  subscribeToRetainerInvoices(organizationId, callback) {
    return supabase?.channel(`retainer-invoices-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'retainer_invoices',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'retainer_invoices',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to recurring invoices changes
  subscribeToRecurringInvoices(organizationId, callback) {
    return supabase?.channel(`recurring-invoices-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'recurring_invoices',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'recurring_invoices',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Subscribe to credit notes changes
  subscribeToCreditNotes(organizationId, callback) {
    return supabase?.channel(`credit-notes-changes-${organizationId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'credit_notes',
        filter: `organization_id=eq.${organizationId}`
      },
      (payload) => {
        callback({
          ...payload,
          table: 'credit_notes',
          eventType: payload?.eventType
        });
      }
    )?.subscribe();
  },

  // Universal subscription manager with unique channel names
  subscribeToAllChanges(organizationId, callbacks) {
    const subscriptions = [];

    if (callbacks?.customers) {
      subscriptions?.push(this?.subscribeToCustomers(organizationId, callbacks?.customers));
    }
    if (callbacks?.invoices) {
      subscriptions?.push(this?.subscribeToInvoices(organizationId, callbacks?.invoices));
    }
    if (callbacks?.quotes) {
      subscriptions?.push(this?.subscribeToQuotes(organizationId, callbacks?.quotes));
    }
    if (callbacks?.payments) {
      subscriptions?.push(this?.subscribeToPayments(organizationId, callbacks?.payments));
    }
    if (callbacks?.paymentLinks) {
      subscriptions?.push(this?.subscribeToPaymentLinks(organizationId, callbacks?.paymentLinks));
    }
    if (callbacks?.deliveryChallans) {
      subscriptions?.push(this?.subscribeToDeliveryChallans(organizationId, callbacks?.deliveryChallans));
    }
    if (callbacks?.retainerInvoices) {
      subscriptions?.push(this?.subscribeToRetainerInvoices(organizationId, callbacks?.retainerInvoices));
    }
    if (callbacks?.recurringInvoices) {
      subscriptions?.push(this?.subscribeToRecurringInvoices(organizationId, callbacks?.recurringInvoices));
    }
    if (callbacks?.creditNotes) {
      subscriptions?.push(this?.subscribeToCreditNotes(organizationId, callbacks?.creditNotes));
    }

    return {
      subscriptions,
      unsubscribeAll: () => {
        subscriptions?.forEach(sub => {
          try {
            sub?.unsubscribe();
          } catch (error) {
            console.warn('Error unsubscribing:', error);
          }
        });
      }
    };
  }
};
